import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Book, BookOpen, CheckCircle, ChevronRight, Volume2, ArrowRight } from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import { getLanguageData } from '../data/languages';
import type {
  GrammarModeProps,
  GrammarCategory,
  WordData,
  GrammarItem,
  UserProgress,
  RevisionSentence,
} from '../types/types';
import { getSentencesForWord } from '../utils/sentencesgapUtils';
import { getWordTranslation, getWordExplanation, getWordExample } from '../utils/localizedContent';
import GrammarConsolidation from './GrammarConsolidation';
import GrammarModuleEval from './GrammarModuleEval';
import './GrammarMode.css';
import './LearnMode.css';
import TribalBorder from './TribalBorder';
import { useTheme } from './ThemeContext';
import { useSupabaseAudio } from '../hooks/useSupabaseAudio';
import { useAudio } from '../hooks/hooks';
import XPAnimation from './XPAnimation';

import ConfirmationModal from './ConfirmationModal';
import SummaryScreen from './SummaryScreen';
import { getRandomCongrats, getRandomProverb } from '../data/summaryData';
import { useSummaryAudio } from '../utils/summaryAudio';

import { EmojiText, Picto } from './common/Picto';
// Constante pour les points XP
const XP_PER_GRAMMAR_RULE = 10;

interface SubcategoryCardProps {
  title: string;
  completed: boolean;
  onClick: () => void;
  count: number;
  completedCount: number;
  mascotTip?: string;
}

const GRAMMAR_MASCOT_TIPS: Record<string, Record<string, string>> = {
  wolof: {
    // Unité 1 — Pronoms
    'Pronoms personnels (ngi)': "Ces pronoms expriment une action en cours, comme le présent continu. «\u00a0Maa ngi lekk\u00a0» = Je suis en train de manger\u00a0!",
    'Pronoms emphatiques': "Ils mettent l'accent sur QUI fait l'action. «\u00a0Maa ko def\u00a0» = C'est MOI qui l'ai fait\u00a0!",
    'Pronoms objets': "Ils désignent qui reçoit l'action. «\u00a0Dama ko jox\u00a0» = Je le lui ai donné. Ils se placent après le verbe.",
    'Pronoms possessifs': "Ils indiquent à qui appartient quelque chose. «\u00a0Sama kër\u00a0» = Ma maison. Ils se placent avant le nom.",
    'Pronoms toniques': "Formes renforcées, souvent en début de phrase ou seuls. «\u00a0Yow rekk\u00a0» = Toi seul. Très utiles pour insister\u00a0!",
    'Pronoms focus verbal (Da-)': "Ils mettent l'accent sur l'ACTION elle-même. «\u00a0Dama lekk\u00a0» = Moi, je mange. Très courants dans la langue parlée\u00a0!",
    // Unité 1 — Bases
    'Classes nominales': "Le wolof classe tous les noms en familles (B, G, M, W…). Chaque classe a son article défini. C'est différent du genre masculin/féminin du français\u00a0!",
    'Structure de la phrase': "En wolof, le FOCUS change tout\u00a0! Selon ce qu'on veut mettre en valeur (sujet, verbe ou complément), la structure de la phrase change complètement.",
    // Unité 2 — Verbes
    'Temps verbaux — Présent': "Le présent en wolof distingue l'habitude (damay…) du progressif (maa ngi…) et de l'imparfait (doon). Chacun a sa nuance\u00a0!",
    'Temps verbaux — Passé': "Le passé wolof est riche\u00a0: résultatif (dem na), emphatique (dafa dem), antérieur (demoon na), habituel révolu (daan na). Chaque forme dit quelque chose de différent\u00a0!",
    'Temps verbaux — Futur': "Le futur se forme avec «\u00a0dina\u00a0» + suffixe de personne. Attention\u00a0: «\u00a0Dinaa\u00a0» = je (1sg), «\u00a0Dina\u00a0» = il/elle (3sg)\u00a0!",
    'Temps verbaux — Impératif & Conditionnel': "Pour ordonner, ajoutez «\u00a0-al\u00a0» (singulier) ou «\u00a0-leen\u00a0» (pluriel) au verbe. Pour interdire, utilisez «\u00a0bul\u00a0» devant le verbe\u00a0!",
    'Négation': "La négation principale en wolof c'est le suffixe «\u00a0-ul\u00a0» ajouté au verbe. Pour le futur négatif, utilisez «\u00a0du-\u00a0» ou «\u00a0du\u00a0» devant le verbe\u00a0!",
    'Questions': "Pour poser une question fermée (oui/non), commencez par «\u00a0Ndax\u00a0». Pour les questions ouvertes, utilisez lan (quoi), kan (qui), foo (où), kañ (quand), naka (comment)\u00a0!",
    // Unité 3 — Vocabulaire grammatical
    'Prépositions de lieu': "La préposition de base est «\u00a0ci\u00a0» (dans/sur/à). Elle se combine avec d'autres mots pour former des locutions\u00a0: ci kaw (sur), ci ron (sous), ci biir (à l'intérieur)…",
    'Prépositions de temps et de relation': "«\u00a0Ak\u00a0» relie des noms (comme «\u00a0et\u00a0»), «\u00a0ngir\u00a0» exprime le but (pour), «\u00a0ndax\u00a0» la cause (parce que). Ils sont très fréquents\u00a0!",
    'Adjectifs — Apparence physique': "En wolof, les adjectifs suivent le verbe «\u00a0na\u00a0» (être/avoir). Ex\u00a0: «\u00a0dafa rafet\u00a0» = il/elle est beau/belle. Ils ne s'accordent pas en genre\u00a0!",
    'Adjectifs — Caractère': "Ces adjectifs décrivent la personnalité. «\u00a0Baax\u00a0» (bon) et «\u00a0muñ\u00a0» (patient) sont des qualités très valorisées dans la culture wolof\u00a0!",
    'Adjectifs — États et sensations': "Ces adjectifs décrivent des états physiques. «\u00a0Dama xiif\u00a0» = J'ai faim (litt. je suis affamé). Notez que «\u00a0neex\u00a0» peut aussi signifier «\u00a0faire plaisir\u00a0»\u00a0!",
    'Adjectifs — Quantité et jugement': "«\u00a0Lool\u00a0» (très/beaucoup) se combine souvent avec ces adjectifs pour intensifier. Ex\u00a0: «\u00a0jafé lool\u00a0» = très difficile. «\u00a0Bees\u00a0» (nouveau) s'oppose à «\u00a0màggat\u00a0» (vieux).",
    'Conjonctions': "«\u00a0Ak\u00a0» relie des noms, «\u00a0te\u00a0» relie des phrases. «\u00a0Waaye\u00a0» = mais, «\u00a0ndax\u00a0» = parce que. «\u00a0Bu\u00a0» et «\u00a0su\u00a0» introduisent des conditions ou du temps\u00a0!",
    'Adverbes': "«\u00a0Lool\u00a0» (très) et «\u00a0rekk\u00a0» (seulement) sont les plus courants. «\u00a0Ndank ndank\u00a0» est aussi un proverbe wolof\u00a0: «\u00a0doucement doucement on va loin\u00a0»\u00a0!",
  },
  bambara: {
    // Unité 1
    'Pronoms sujets': "En bambara, le pronom sujet précède toujours la particule verbale. Il n'y a pas de genre\u00a0: «\u00a0A\u00a0» signifie «\u00a0il\u00a0» ET «\u00a0elle\u00a0». Simple et efficace\u00a0!",
    'Pronoms objets': "Le pronom objet se glisse entre la particule verbale et le verbe. Ex\u00a0: «\u00a0A bɛ n dɛmɛ\u00a0» = Il m'aide (litt. il PART. moi aider). Ordre\u00a0: Sujet – Particule – Objet – Verbe.",
    'Pronoms possessifs': "En bambara, le possessif se forme avec «\u00a0ka\u00a0» placé après le pronom. «\u00a0N ka\u00a0» = mon/ma, «\u00a0I ka\u00a0» = ton/ta, «\u00a0A ka\u00a0» = son/sa. Toujours avant le nom possédé\u00a0!",
    'Pronoms emphatiques et réfléchis': "«\u00a0N kɔ\u00a0» souligne le pronom (moi-même en tant que sujet connu). «\u00a0N yɛrɛ\u00a0» = moi-même (réfléchi). Ces formes renforcent l'identité de celui qui agit\u00a0!",
    'Pronoms Interrogatifs': "«\u00a0Jɔn\u00a0» = qui, «\u00a0Mun\u00a0» = quoi/quel, «\u00a0Min\u00a0» introduit les relatives. Ces pronoms restent invariables\u00a0: pas d'accord en genre ni en nombre en bambara\u00a0!",
    'Structure de la phrase': "Le bambara suit l'ordre Sujet – Objet – Verbe (SOV)\u00a0! Très différent du français. «\u00a0N dɔgɔ ye sogo dun\u00a0» = Mon frère a mangé de la viande (litt. mon frère viande mangé).",
    // Unité 2
    'Particules verbales': "Les particules verbales sont le cœur du système bambara\u00a0! Elles indiquent le temps et l'aspect. «\u00a0Bɛ\u00a0» = présent, «\u00a0Ye\u00a0» = passé transitif, «\u00a0Ra\u00a0» = passé intransitif, «\u00a0Bɛna\u00a0» = futur.",
    'Temps verbaux — Présent': "Le présent bambara s'exprime avec «\u00a0bɛ\u00a0» pour toutes les personnes. Il n'y a pas de conjugaison du verbe\u00a0: seul le pronom change\u00a0! «\u00a0N bɛ taa\u00a0» / «\u00a0I bɛ taa\u00a0» / «\u00a0A bɛ taa\u00a0».",
    'Temps verbaux — Passé': "Le passé a deux formes\u00a0: «\u00a0ye\u00a0» (transitif, avec objet) et «\u00a0ra/la\u00a0» (intransitif). «\u00a0tun bɛ\u00a0» = imparfait, «\u00a0tun ye\u00a0» = plus-que-parfait. Le choix dépend du verbe, pas du temps\u00a0!",
    'Temps verbaux — Futur': "Le futur se forme avec «\u00a0bɛna\u00a0» pour toutes les personnes. Aucun accord\u00a0: «\u00a0N bɛna taa\u00a0» / «\u00a0U bɛna taa\u00a0». Régulier et facile à apprendre\u00a0!",
    'Temps verbaux — Impératif & Subjonctif': "L'impératif bambara est simple\u00a0: utilisez le verbe seul pour le singulier, «\u00a0aw\u00a0» + verbe pour le pluriel. «\u00a0Ka\u00a0» introduit l'infinitif/subjonctif. «\u00a0Kana\u00a0» = ne pas (défense)\u00a0!",
    'Négation': "La négation varie selon la particule\u00a0: «\u00a0tɛ\u00a0» au présent (remplace «\u00a0bɛ\u00a0»), «\u00a0ma\u00a0» au passé, «\u00a0tɛna\u00a0» au futur. Une particule, une règle. Logique et régulier\u00a0!",
    // Unité 3
    'Postpositions de lieu': "En bambara, les prépositions se placent APRÈS le nom (postpositions)\u00a0! «\u00a0Sò la\u00a0» = dans la maison, «\u00a0Mesa kan\u00a0» = sur la table. C'est l'inverse du français\u00a0!",
    'Postpositions de temps et de cause': "«\u00a0Tuma min\u00a0» = quand/lorsque, «\u00a0Sani\u00a0» = avant de, «\u00a0Bari\u00a0» = parce que. Ces mots organisent le récit dans le temps et expliquent les causes.",
    'Adjectifs — Apparence et taille': "En bambara, les adjectifs suivent directement le nom qu'ils qualifient, sans accord. «\u00a0Cɛ bɑ\u00a0» = grand homme. «\u00a0Bɑ\u00a0» (grand) et «\u00a0fɛ\u00a0» (beau) sont parmi les plus courants\u00a0!",
    'Adjectifs — Caractère et comportement': "Ces adjectifs décrivent la personnalité. «\u00a0Nɔgɔnya\u00a0» (travailleur) et «\u00a0dɛmɛbali\u00a0» (sans aide = autonome) révèlent les valeurs sociales du bambara\u00a0!",
    'Adjectifs — États et émotions': "«\u00a0A bɛ dusu kɛ\u00a0» = Il est triste (litt. il fait cœur). En bambara, les émotions s'expriment souvent avec «\u00a0dusu\u00a0» (cœur/ventre). Très expressif\u00a0!",
    'Adjectifs — Goût et sensations': "«\u00a0Duman\u00a0» (délicieux) et «\u00a0farimaɲɛ\u00a0» (sain/fort) sont très courants. Ces adjectifs décrivent les sensations physiques et les perceptions des sens\u00a0!",
    'Adjectifs — Quantité et valeur': "«\u00a0Caman\u00a0» = beaucoup, «\u00a0dɔɔnin\u00a0» = peu. Ces mots servent aussi d'adverbes\u00a0! «\u00a0Ɲuman\u00a0» (bon/bien) est l'un des mots les plus utilisés du bambara\u00a0!",
    'Conjonctions': "«\u00a0Ni\u00a0» = si/et, «\u00a0Nka\u00a0» = mais, «\u00a0Walima\u00a0» = ou. «\u00a0Ko\u00a0» introduit le discours indirect (que). «\u00a0Ni…bɛ\u00a0» forme les conditionnelles\u00a0: Si…alors.",
    'Adverbes': "«\u00a0Sisan\u00a0» = maintenant, «\u00a0Kosɛbɛ\u00a0» = vraiment/beaucoup (intensificateur). «\u00a0Dɔɔnin dɔɔnin\u00a0» (petit à petit) est l'équivalent bambara du proverbe «\u00a0doucement doucement on va loin\u00a0»\u00a0!",
  },
  la: {
    // ── LINGALA — Unité 1 ──────────────────────────────────────────
    'Pronoms sujets': "En lingala, chaque pronom correspond à un préfixe verbal\u00a0: ngai → na-, yo → o-, ye → a-, biso → to-, bino → bo-, bango → ba-. Ce préfixe est collé directement au verbe\u00a0!",
    'Pronoms objets': "En lingala, les pronoms objets sont les mêmes formes que les sujets, mais placés APRÈS le verbe. «\u00a0Nabonaki ye\u00a0» = Je l'ai vu. Ordre\u00a0: Sujet + Verbe + Objet.",
    'Pronoms possessifs': "La possession se forme avec «\u00a0ya\u00a0» + pronom, placé APRÈS le nom. «\u00a0Ndako ya ngai\u00a0» = ma maison (litt. maison de moi). L'inverse du français\u00a0!",
    'Pronoms démonstratifs': "«\u00a0Oyo\u00a0» (celui-ci) et «\u00a0wana\u00a0» (celui-là) suivent toujours le nom. «\u00a0Moto oyo\u00a0» = cette personne. Ils s'accordent avec les classes nominales bantoues\u00a0!",
    'Pronoms interrogatifs': "«\u00a0Nani\u00a0» = qui, «\u00a0nini\u00a0» = quoi, «\u00a0wapi\u00a0» = où. Pour les questions de temps ou de manière, on combine\u00a0: «\u00a0tango nini\u00a0» = quand, «\u00a0ndenge nini\u00a0» = comment. Très régulier\u00a0!",
    'Structure de la phrase': "Le lingala suit l'ordre SVO comme le français\u00a0! La grande différence\u00a0: la négation «\u00a0te\u00a0» se place en FIN de phrase, et la possession utilise «\u00a0ya\u00a0» après le nom.",

    // ── LINGALA — Unité 2 ──────────────────────────────────────────
    'Temps verbaux — Présent progressif': "Le présent progressif se forme avec\u00a0: préfixe + zali + ko- + verbe. «\u00a0Nazali koliya\u00a0» = Je suis en train de manger. «\u00a0Ko-\u00a0» marque l'action en cours\u00a0!",
    'Temps verbaux — Présent habituel': "L'habitude se marque avec le suffixe «\u00a0-aka\u00a0» ajouté au verbe. «\u00a0Naliyaka\u00a0» = Je mange (habituellement). Ce suffixe est régulier pour toutes les personnes\u00a0!",
    'Temps verbaux — Passé': "Le passé se forme avec le suffixe «\u00a0-aki\u00a0» ajouté au verbe. «\u00a0Nakendaki\u00a0» = Je suis allé, «\u00a0naliyaki\u00a0» = j'ai mangé. Régulier pour tous les verbes et toutes les personnes\u00a0!",
    'Temps verbaux — Futur': "Le futur se forme avec «\u00a0ko-\u00a0» intercalé entre le préfixe et le verbe. «\u00a0Nakokende\u00a0» = J'irai (na + ko + kende). Attention\u00a0: «\u00a0ko-\u00a0» marque aussi l'infinitif\u00a0!",
    'Temps verbaux — Impératif': "L'impératif singulier\u00a0: retirez le préfixe et «\u00a0ko-\u00a0». Ko-kende → «\u00a0kende\u00a0»\u00a0! Pour le pluriel, ajoutez «\u00a0bo-\u00a0». Pour interdire\u00a0: gardez «\u00a0ko-\u00a0» et ajoutez «\u00a0te\u00a0» à la fin.",
    'Négation': "La règle d'or du lingala\u00a0: ajoutez «\u00a0te\u00a0» à la FIN de la phrase. Nazali koliya te, nakendaki te, nakokende te… C'est la même règle pour tous les temps\u00a0!",

    // ── LINGALA — Unité 3 ──────────────────────────────────────────
    'Prépositions de lieu': "La préposition «\u00a0na\u00a0» est la plus polyvalente (à, dans, en, avec). Pour préciser, on la combine\u00a0: «\u00a0kati na\u00a0» (dans), «\u00a0likolo ya\u00a0» (sur), «\u00a0nse ya\u00a0» (sous).",
    'Prépositions de temps et de relation': "«\u00a0Sima ya\u00a0» = après, «\u00a0liboso ya\u00a0» = avant, «\u00a0banda\u00a0» = depuis. «\u00a0Elongo na\u00a0» (avec/ensemble) est très expressif dans la culture lingala\u00a0: l'unité est une valeur centrale\u00a0!",
    'Adjectifs — Apparence et taille': "En lingala, les adjectifs suivent directement le nom\u00a0: «\u00a0mwana moke\u00a0» = petit enfant. «\u00a0Kitoko\u00a0» (beau) et «\u00a0monene\u00a0» (grand) sont parmi les plus fréquents\u00a0!",
    'Adjectifs — Qualité et état': "«\u00a0Malamu\u00a0» (bon/bien) est l'adjectif le plus utilisé du lingala\u00a0! Il sert aussi de réponse universelle. «\u00a0Ozali malamu\u00a0?» — «\u00a0Nazali malamu\u00a0!» (Tu vas bien? — Je vais bien\u00a0!)",
    'Adjectifs — Caractère': "«\u00a0Makasi\u00a0» (fort/intense) est très expressif et s'utilise pour amplifier n'importe quel adjectif. «\u00a0Malamu makasi\u00a0» = vraiment bien. «\u00a0Ya sika\u00a0» (nouveau) et «\u00a0ya kala\u00a0» (vieux) fonctionnent avec n'importe quel nom\u00a0!",
    'Conjonctions': "«\u00a0Mpe\u00a0» (et), «\u00a0kasi\u00a0» (mais), «\u00a0soki\u00a0» (si). «\u00a0Atako\u00a0» (bien que) est très expressif\u00a0: «\u00a0Atako azali moke, azali makasi\u00a0» = Bien qu'il soit petit, il est fort\u00a0!",
    'Adverbes': "«\u00a0Mingi\u00a0» (beaucoup/très) est l'adverbe le plus courant du lingala\u00a0! «\u00a0Nalingi yo mingi\u00a0» = Je t'aime beaucoup. «\u00a0Tango nyonso\u00a0» (toujours) et «\u00a0seko\u00a0» (jamais) sont les deux extrêmes de la fréquence.",
  },
  ff: {
    // ── PEUL — Unité 1 ──────────────────────────────────────────
    'Pronoms personnels (ɗon)': "En peul, le marqueur progressif «\u00a0miɗo/aɗa/omo\u00a0» se place entre le sujet et le verbe. «\u00a0Miɗo yaha\u00a0» = Je suis en train d'aller. C'est l'équivalent du présent continu\u00a0!",
    'Pronoms emphatiques': "Pour insister sur QUI fait l'action, utilisez les formes longues\u00a0: Miin (moi), Aan (toi), Kanko (lui/elle). Ex\u00a0: «\u00a0Miin mi waɗii ko\u00a0» = C'est MOI qui l'ai fait. Très expressif\u00a0!",
    'Pronoms objets': "Les pronoms objets sont des suffixes attachés au verbe en peul\u00a0! «\u00a0Dimmal-am\u00a0» = Aide-moi, «\u00a0rokki-mo\u00a0» = je lui ai donné. Le suffixe change selon la personne (-am, -ma, -mo...).",
    'Pronoms possessifs': "La possession se marque APRÈS le nom en peul. «\u00a0Galle am\u00a0» = Ma maison (litt. maison mon). Les formes\u00a0: am (mon), maa (ton), makko (son), amen (notre), mooɗon (votre), maɓɓe (leur).",
    'Pronoms sujets simples': "Les pronoms simples mi/a/o/minen/onon/ɓe s'utilisent surtout au passé et à l'impératif. «\u00a0Mi yahii\u00a0» = Je suis allé. Au présent progressif, ils sont remplacés par miɗo/aɗa/omo\u00a0!",
    'Marqueurs de focus (Ko)': "«\u00a0Ko\u00a0» est la copule de focus du peul. Elle met en relief l'identité du sujet. «\u00a0Ko miin mi waɗiri\u00a0» = C'est MOI qui l'ai fait. Très courant dans la langue parlée\u00a0!",
    'Classes nominales': "Le peul a plus de 20 classes nominales\u00a0! Chaque classe a son singulier et son pluriel\u00a0: gorko/worɓe (homme/hommes), nagge/na'i (vache/vaches). C'est l'une des langues les plus riches en classes au monde\u00a0!",
    'Structure de la phrase': "L'ordre de base en peul est\u00a0: Sujet + Marqueur + Verbe + Complément. Le focus change la structure\u00a0: «\u00a0Ko miin\u00a0» met le sujet en valeur. Le verbe «\u00a0woni\u00a0» = être est indispensable\u00a0!",
    // ── PEUL — Unité 2 ──────────────────────────────────────────
    'Temps verbaux — Présent': "Le présent en peul distingue le progressif (miɗo yaha = je vais maintenant) de l'habituel (mi jangay = j'étudie habituellement). Le suffixe «\u00a0-ay\u00a0» marque l'habitude\u00a0!",
    'Temps verbaux — Passé': "Le passé accompli se forme avec le suffixe «\u00a0-ii\u00a0»\u00a0: mi yahii = je suis allé. L'imparfait utilise «\u00a0doon\u00a0» (mi doon yaha). Le passé habituel révolu utilise «\u00a0daan\u00a0» (mi daan yaha = j'avais l'habitude d'aller).",
    'Temps verbaux — Futur': "Le futur se forme avec un changement de la voyelle finale du verbe + suffixe\u00a0: yaha (aller) → yehay (ira). Au pluriel, le préfixe «\u00a0nj-\u00a0» s'ajoute\u00a0: minen njehay = nous irons. Conditionnel avec «\u00a0so\u00a0» (si)\u00a0!",
    'Temps verbaux — Impératif': "L'impératif singulier se forme avec une terminaison spéciale (-du)\u00a0: Yahdu! = Va! Au pluriel\u00a0: Njahon! Le préfixe «\u00a0hoto\u00a0» interdit l'action\u00a0: Hoto yahdu! = Ne va pas\u00a0!",
    'Négation': "La négation du présent habituel se forme avec le suffixe «\u00a0-aa\u00a0» après le verbe\u00a0: mi yahaa = je ne vais pas. Pour le passé, on utilise «\u00a0-iino\u00a0». Pour le futur, on ajoute «\u00a0-aa\u00a0» à la forme future\u00a0!",
    'Questions': "Les mots interrogatifs essentiels\u00a0: Hommbo (qui), Honto (où), Honnde (quand), Honno (comment), Fii hondun (pourquoi), Noy (combien). Pour les questions oui/non, utilisez «\u00a0Ndax\u00a0» en début de phrase\u00a0!",
    // ── PEUL — Unité 3 ──────────────────────────────────────────
    'Prépositions de lieu': "La préposition de base «\u00a0to\u00a0» indique la direction (comme «\u00a0à\u00a0» en français). «\u00a0E\u00a0» marque la position. Pour préciser\u00a0: dow (sur), ley (sous), nder (à l'intérieur), caggal (derrière), yeeso (devant)\u00a0!",
    'Prépositions de temps et de relation': "«\u00a0Caggal\u00a0» = après, «\u00a0yeeso\u00a0» = avant, «\u00a0ngir\u00a0» = pour. «\u00a0Ko\u00a0» exprime l'accompagnement (avec). «\u00a0Ni\u00a0» indique la comparaison\u00a0: «\u00a0Dox ni puccu\u00a0» = Cours comme un cheval\u00a0!",
    'Adjectifs — Apparence physique': "En peul, les adjectifs s'accordent avec la classe nominale du nom\u00a0! «\u00a0Moƴƴo\u00a0» (beau/bon) et «\u00a0bonngo\u00a0» (mauvais) sont les plus courants. «\u00a0Mawɗo\u00a0» (grand) et «\u00a0pamaro\u00a0» (petit) s'utilisent aussi pour l'importance\u00a0!",
    'Adjectifs — Caractère': "«\u00a0ɗoole\u00a0» (fort/courageux) et «\u00a0goonga\u00a0» (honnête) sont des valeurs très valorisées dans la culture peule. «\u00a0Muɗɗo\u00a0» (patient) reflète la patience, vertu centrale dans la philosophie «\u00a0pulaaku\u00a0»\u00a0!",
    'Adjectifs — États et sensations': "«\u00a0Miɗo hebla\u00a0» = Je suis fatigué(e). «\u00a0Mi anndinɗo\u00a0» = J'ai faim. Les sensations physiques se construisent souvent avec le pronom progressif miɗo ou le verbe woni\u00a0!",
    'Adjectifs — Quantité et jugement': "«\u00a0Keewɓe\u00a0» (nombreux) varie selon la classe nominale. «\u00a0Seeɗa\u00a0» = peu — très courant au quotidien. «\u00a0Seeɗa seeɗa\u00a0» (petit à petit) est aussi un adverbe de manière\u00a0!",
    'Conjonctions': "«\u00a0E\u00a0» relie deux noms (et), «\u00a0kono\u00a0» introduit une opposition (mais), «\u00a0so\u00a0» une condition (si). «\u00a0Ndaw\u00a0» exprime la cause (parce que). Ces mots structurent le récit en peul\u00a0!",
    'Adverbes': "«\u00a0Tan\u00a0» (seulement) et «\u00a0tawi\u00a0» (vraiment) sont très fréquents. «\u00a0Seeɗa seeɗa\u00a0» (doucement, pas à pas) est le proverbe peul de l'apprentissage progressif. «\u00a0Haapoma\u00a0» = jamais\u00a0!",
  },
  sw: {
    // ── SWAHILI — Unité 1 ──────────────────────────────────────────
    'Pronoms sujets': "En swahili, les pronoms sujets sont des PRÉFIXES collés au verbe\u00a0: Ni-na-soma = Je (Ni) – présent (na) – lire (soma). Chaque personne a son préfixe\u00a0: Ni, U, A, Tu, M, Wa. Très régulier\u00a0!",
    'Pronoms emphatiques': "Les formes longues Mimi, Wewe, Yeye, Sisi, Ninyi, Wao mettent l'accent sur le sujet. «\u00a0Mimi ndiye niliyefanya\u00a0» = C'est MOI qui l'ai fait. «\u00a0Ndiye\u00a0» (pour les personnes) est le marqueur de focus\u00a0!",
    'Pronoms objets': "Les pronoms objets sont des infixes insérés dans le verbe\u00a0: Ni-na-ku-ona = Je te vois (Ni + na + ku + ona). L'ordre est\u00a0: préfixe sujet + temps + objet + verbe. Très compact\u00a0!",
    'Pronoms possessifs': "La possession s'accorde avec la classe nominale du nom possédé\u00a0! «\u00a0Yangu\u00a0» pour nyumba (maison), «\u00a0changu\u00a0» pour kitabu (livre). La racine reste la même (-angu, -ako, -ake...), seul le préfixe change\u00a0!",
    'Classes nominales': "Le swahili a 8 classes nominales\u00a0! La plus importante\u00a0: M/Wa pour les personnes (mtu/watu). Ki/Vi pour les langues et objets (Kiswahili). N/N est invariable. Ces classes changent TOUS les accords grammaticaux\u00a0!",
    'Structure de la phrase': "Le swahili suit l'ordre SVO comme le français\u00a0! La différence\u00a0: tout s'agglutine dans le verbe. «\u00a0Ni-na-soma\u00a0» = Je lis. La concordance nominale signifie que l'adjectif s'accorde avec la classe du nom\u00a0!",
    // ── SWAHILI — Unité 2 ──────────────────────────────────────────
    'Temps verbaux — Présent': "Le marqueur du présent est «\u00a0na\u00a0»\u00a0: Ninasoma = Je lis. Le temps habituel utilise «\u00a0hu\u00a0» sans préfixe de personne\u00a0: Husoma = On lit habituellement. Deux formes pour deux nuances différentes\u00a0!",
    'Temps verbaux — Passé': "Le passé simple utilise «\u00a0li\u00a0»\u00a0: Nilisoma = J'ai lu. Le parfait utilise «\u00a0me\u00a0»\u00a0: Nimesoma = J'ai lu (et le résultat est encore présent). L'imparfait combine «\u00a0nilikuwa nikisoma\u00a0» = j'étais en train de lire\u00a0!",
    'Temps verbaux — Futur': "Le futur se forme avec «\u00a0ta\u00a0»\u00a0: Nitasoma = Je lirai. Le conditionnel «\u00a0ki\u00a0» signifie «\u00a0si\u00a0»\u00a0: Nikisoma = si je lis. «\u00a0Nge\u00a0» exprime l'hypothétique\u00a0: Ningesoma = Je lirais. Systématique et régulier\u00a0!",
    'Temps verbaux — Impératif': "L'impératif singulier = racine verbale seule\u00a0: Soma! = Lis! Le pluriel ajoute «\u00a0-ni\u00a0»\u00a0: Someni! Pour interdire au singulier\u00a0: Usi + verbe (Usisome!). Au pluriel\u00a0: Msi + verbe (Msisome!). Simple\u00a0!",
    'Négation': "La négation transforme le préfixe sujet\u00a0: Ni → Si, U → Hu, A → Ha, Tu → Hatu, M → Ham, Wa → Hawa. Sisomi = Je ne lis pas. Pour le passé\u00a0: Sikuenda = Je ne suis pas allé. Pour le parfait\u00a0: Sijasoma = Je n'ai pas encore lu\u00a0!",
    'Questions': "Nani (qui), Nini (quoi), Wapi (où), Lini (quand), Vipi (comment), Kwa nini (pourquoi), Ngapi (combien). Pour une question oui/non, commencez par «\u00a0Je\u00a0»\u00a0: Je, unaenda? = Est-ce que tu y vas\u00a0?",
    // ── SWAHILI — Unité 3 ──────────────────────────────────────────
    'Prépositions de lieu': "Les locutions de lieu se forment avec «\u00a0ya\u00a0»\u00a0: Juu ya (sur), Chini ya (sous), Mbele ya (devant), Nyuma ya (derrière). «\u00a0Katika\u00a0» = dans. «\u00a0Karibu na\u00a0» = près de, «\u00a0mbali na\u00a0» = loin de\u00a0!",
    'Prépositions de temps et de relation': "«\u00a0Baada ya\u00a0» = après, «\u00a0kabla ya\u00a0» = avant, «\u00a0tangu\u00a0» = depuis, «\u00a0hadi\u00a0» = jusqu'à. «\u00a0Kwa\u00a0» est la préposition la plus polyvalente\u00a0: kwa daktari = chez le médecin, kwa gari = en voiture\u00a0!",
    'Adjectifs — Apparence physique': "Les adjectifs s'accordent avec la classe nominale\u00a0! «\u00a0Mzuri\u00a0» pour M/Wa, «\u00a0nzuri\u00a0» pour N, «\u00a0zuri\u00a0» pour Ji/Ma... Mkubwa (grand), mdogo (petit), mrefu (long), mfupi (court). La racine reste, le préfixe change\u00a0!",
    'Adjectifs — Caractère': "«\u00a0Mvumilivu\u00a0» (patient) et «\u00a0mwaminifu\u00a0» (fidèle) sont très valorisés dans la culture swahili. «\u00a0Mwerevu\u00a0» (intelligent/malin) s'utilise aussi pour quelqu'un de futé. «\u00a0Mpole\u00a0» = doux et poli\u00a0!",
    'Adjectifs — États et sensations': "Les sensations se construisent souvent avec «\u00a0Nina\u00a0»\u00a0: Nina njaa = J'ai faim, Nina joto = J'ai chaud. «\u00a0Mchoka\u00a0» = fatigué. «\u00a0Tamu\u00a0» (délicieux) est le compliment ultime pour la nourriture swahili\u00a0!",
    'Adjectifs — Quantité et jugement': "«\u00a0Nyingi\u00a0» (nombreux) et «\u00a0chache\u00a0» (peu) s'accordent avec les classes. «\u00a0Rahisi\u00a0» (facile) signifie aussi «\u00a0bon marché\u00a0»\u00a0! «\u00a0Sana\u00a0» (très) s'ajoute après tout adjectif\u00a0: nzuri sana = très beau\u00a0!",
    'Conjonctions': "«\u00a0Na\u00a0» (et/avec), «\u00a0lakini\u00a0» (mais), «\u00a0au\u00a0» (ou), «\u00a0kwa sababu\u00a0» (parce que). «\u00a0Ingawa\u00a0» (bien que) permet d'exprimer des concessions. «\u00a0Kama\u00a0» = si/comme — un mot pour deux fonctions\u00a0!",
    'Adverbes': "«\u00a0Sana\u00a0» (très/beaucoup) est l'adverbe le plus courant en swahili\u00a0! «\u00a0Polepole\u00a0» (doucement) est aussi un proverbe\u00a0: «\u00a0Haraka haraka haina baraka\u00a0» = La précipitation n'apporte pas de bénédiction. «\u00a0Bado\u00a0» = encore/pas encore\u00a0!",
  },
  yo: {
    // ── YORUBA — Unité 1 ──────────────────────────────────────────
    "Pronoms possessifs — Le mien, le tien": "La règle : Tí (« celui de ») + pronom. En se collant au pronom, Tí perd son « í » : Tí + Èmi → t’èmi, l’apostrophe marque la voyelle disparue. Pas de masculin ni de féminin : t’òun = à lui ou à elle. Pour « le tien », t’ìwọ et t’ìrẹ ont le même sens. Pour faire une phrase : … ni (c’est …), … nìyẹn (c’est … ça), … l’èyí (ceci est …), … kọ́ (ce n’est pas …). Exemples : Ìwé yìí kì í ṣe t’èmi = Ce livre n’est pas à moi. Ṣé ife yìí jẹ́ t’ìrẹ? = Cette tasse est-elle à toi ? T’àwọn ni mo mú = C’est le leur que j’ai pris.",
  },
};

const GRAMMAR_MASCOT_TIPS_EN: Record<string, Record<string, string>> = {
  wolof: {
    // Unit 1 — Pronouns
    'Pronoms personnels (ngi)': "These pronouns express an ongoing action, like the present continuous. « Maa ngi lekk » = I am eating !",
    'Pronoms emphatiques': "They emphasize WHO performs the action. « Maa ko def » = It was ME who did it !",
    'Pronoms objets': "They indicate who receives the action. « Dama ko jox » = I gave it to him/her. They're placed after the verb.",
    'Pronoms possessifs': "They indicate who owns something. « Sama kër » = My house. They're placed before the noun.",
    'Pronoms toniques': "Reinforced forms, often used at the start of a sentence or on their own. « Yow rekk » = You alone. Very useful for emphasis !",
    'Pronoms focus verbal (Da-)': "They put the emphasis on the ACTION itself. « Dama lekk » = As for me, I eat. Very common in spoken language !",
    // Unit 1 — Basics
    'Classes nominales': "Wolof sorts all nouns into families (B, G, M, W…). Each class has its own definite article. It's different from French masculine/feminine gender !",
    'Structure de la phrase': "In Wolof, FOCUS changes everything ! Depending on what you want to highlight (subject, verb, or object), the sentence structure changes completely.",
    // Unit 2 — Verbs
    'Temps verbaux — Présent': "The present tense in Wolof distinguishes habitual action (damay…) from the progressive (maa ngi…) and the imperfect (doon). Each has its own nuance !",
    'Temps verbaux — Passé': "The Wolof past is rich : resultative (dem na), emphatic (dafa dem), anterior (demoon na), discontinued habitual (daan na). Each form says something different !",
    'Temps verbaux — Futur': "The future is formed with « dina » + a personal suffix. Careful : « Dinaa » = I (1sg), « Dina » = he/she (3sg) !",
    'Temps verbaux — Impératif & Conditionnel': "To give a command, add « -al » (singular) or « -leen » (plural) to the verb. To forbid, use « bul » before the verb !",
    'Négation': "The main negation in Wolof is the suffix « -ul » added to the verb. For the negative future, use « du- » or « du » before the verb !",
    'Questions': "To ask a closed (yes/no) question, start with « Ndax ». For open questions, use lan (what), kan (who), foo (where), kañ (when), naka (how) !",
    // Unit 3 — Grammar vocabulary
    'Prépositions de lieu': "The basic preposition is « ci » (in/on/at). It combines with other words to form phrases : ci kaw (on top of), ci ron (under), ci biir (inside)…",
    'Prépositions de temps et de relation': "« Ak » links nouns (like 'and'), « ngir » expresses purpose (for), « ndax » expresses cause (because). They're very common !",
    'Adjectifs — Apparence physique': "In Wolof, adjectives follow the verb « na » (to be/have). E.g. : « dafa rafet » = he/she is beautiful/handsome. They don't agree in gender !",
    'Adjectifs — Caractère': "These adjectives describe personality. « Baax » (good) and « muñ » (patient) are highly valued qualities in Wolof culture !",
    'Adjectifs — États et sensations': "These adjectives describe physical states. « Dama xiif » = I'm hungry (lit. I am starving). Note that « neex » can also mean 'to be pleasing' !",
    'Adjectifs — Quantité et jugement': "« Lool » (very/a lot) often combines with these adjectives to intensify them. E.g. : « jafé lool » = very difficult. « Bees » (new) is the opposite of « màggat » (old).",
    'Conjonctions': "« Ak » links nouns, « te » links sentences. « Waaye » = but, « ndax » = because. « Bu » and « su » introduce conditions or time clauses !",
    'Adverbes': "« Lool » (very) and « rekk » (only) are the most common. « Ndank ndank » is also a Wolof proverb : 'slowly, slowly, you go far' !",
  },
  bambara: {
    // Unit 1
    'Pronoms sujets': "In Bambara, the subject pronoun always precedes the verbal particle. There's no gender : « A » means both 'he' AND 'she'. Simple and efficient !",
    'Pronoms objets': "The object pronoun slips in between the verbal particle and the verb. E.g. : « A bɛ n dɛmɛ » = He helps me (lit. he PART. me help). Order : Subject – Particle – Object – Verb.",
    'Pronoms possessifs': "In Bambara, possession is formed with « ka » placed after the pronoun. « N ka » = my, « I ka » = your, « A ka » = his/her. Always before the possessed noun !",
    'Pronoms emphatiques et réfléchis': "« N kɔ » emphasizes the pronoun (myself as the known subject). « N yɛrɛ » = myself (reflexive). These forms reinforce the identity of whoever is acting !",
    'Pronoms Interrogatifs': "« Jɔn » = who, « Mun » = what/which, « Min » introduces relative clauses. These pronouns stay invariable : no agreement in gender or number in Bambara !",
    'Structure de la phrase': "Bambara follows Subject – Object – Verb (SOV) order ! Very different from French. « N dɔgɔ ye sogo dun » = My brother ate meat (lit. my brother meat ate).",
    // Unit 2
    'Particules verbales': "Verbal particles are at the heart of the Bambara system ! They indicate tense and aspect. « Bɛ » = present, « Ye » = transitive past, « Ra » = intransitive past, « Bɛna » = future.",
    'Temps verbaux — Présent': "The Bambara present is expressed with « bɛ » for every person. The verb itself is never conjugated : only the pronoun changes ! « N bɛ taa » / « I bɛ taa » / « A bɛ taa ».",
    'Temps verbaux — Passé': "The past has two forms : « ye » (transitive, with an object) and « ra/la » (intransitive). « tun bɛ » = imperfect, « tun ye » = pluperfect. The choice depends on the verb, not the tense !",
    'Temps verbaux — Futur': "The future is formed with « bɛna » for every person. No agreement needed : « N bɛna taa » / « U bɛna taa ». Regular and easy to learn !",
    'Temps verbaux — Impératif & Subjonctif': "The Bambara imperative is simple : use the bare verb for the singular, « aw » + verb for the plural. « Ka » introduces the infinitive/subjunctive. « Kana » = don't (prohibition) !",
    'Négation': "Negation varies by particle : « tɛ » in the present (replaces « bɛ »), « ma » in the past, « tɛna » in the future. One particle, one rule. Logical and regular !",
    // Unit 3
    'Postpositions de lieu': "In Bambara, prepositions are placed AFTER the noun (postpositions) ! « Sò la » = in the house, « Mesa kan » = on the table. That's the opposite of French !",
    'Postpositions de temps et de cause': "« Tuma min » = when, « Sani » = before, « Bari » = because. These words organize the narrative in time and explain causes.",
    'Adjectifs — Apparence et taille': "In Bambara, adjectives directly follow the noun they describe, with no agreement. « Cɛ bɑ » = tall man. « Bɑ » (big) and « fɛ » (beautiful) are among the most common !",
    'Adjectifs — Caractère et comportement': "These adjectives describe personality. « Nɔgɔnya » (hardworking) and « dɛmɛbali » (without help = self-reliant) reveal the social values of Bambara culture !",
    'Adjectifs — États et émotions': "« A bɛ dusu kɛ » = He/she is sad (lit. he/she makes heart). In Bambara, emotions are often expressed with « dusu » (heart/belly). Very expressive !",
    'Adjectifs — Goût et sensations': "« Duman » (delicious) and « farimaɲɛ » (healthy/strong) are very common. These adjectives describe physical sensations and how the senses perceive things !",
    'Adjectifs — Quantité et valeur': "« Caman » = a lot, « dɔɔnin » = a little. These words also work as adverbs ! « Ɲuman » (good/well) is one of the most used words in Bambara !",
    'Conjonctions': "« Ni » = if/and, « Nka » = but, « Walima » = or. « Ko » introduces reported speech (that). « Ni…bɛ » forms conditionals : If…then.",
    'Adverbes': "« Sisan » = now, « Kosɛbɛ » = really/a lot (intensifier). « Dɔɔnin dɔɔnin » (little by little) is the Bambara equivalent of the proverb 'slowly, slowly, you go far' !",
  },
  la: {
    // ── LINGALA — Unit 1 ──────────────────────────────────────────
    'Pronoms sujets': "In Lingala, each pronoun corresponds to a verbal prefix : ngai → na-, yo → o-, ye → a-, biso → to-, bino → bo-, bango → ba-. This prefix attaches directly to the verb !",
    'Pronoms objets': "In Lingala, object pronouns use the same forms as subject pronouns, but placed AFTER the verb. « Nabonaki ye » = I saw him/her. Order : Subject + Verb + Object.",
    'Pronoms possessifs': "Possession is formed with « ya » + pronoun, placed AFTER the noun. « Ndako ya ngai » = my house (lit. house of me). The opposite of French !",
    'Pronoms démonstratifs': "« Oyo » (this one) and « wana » (that one) always follow the noun. « Moto oyo » = this person. They agree with the Bantu noun classes !",
    'Pronoms interrogatifs': "« Nani » = who, « nini » = what, « wapi » = where. For questions of time or manner, you combine them : « tango nini » = when, « ndenge nini » = how. Very regular !",
    'Structure de la phrase': "Lingala follows SVO order, just like French ! The big difference : the negation « te » goes at the END of the sentence, and possession uses « ya » after the noun.",

    // ── LINGALA — Unit 2 ──────────────────────────────────────────
    'Temps verbaux — Présent progressif': "The present progressive is formed with : prefix + zali + ko- + verb. « Nazali koliya » = I am eating. « Ko- » marks the action in progress !",
    'Temps verbaux — Présent habituel': "Habitual action is marked with the suffix « -aka » added to the verb. « Naliyaka » = I eat (habitually). This suffix is regular for every person !",
    'Temps verbaux — Passé': "The past is formed with the suffix « -aki » added to the verb. « Nakendaki » = I went, « naliyaki » = I ate. Regular for all verbs and all persons !",
    'Temps verbaux — Futur': "The future is formed with « ko- » inserted between the prefix and the verb. « Nakokende » = I will go (na + ko + kende). Watch out : « ko- » also marks the infinitive !",
    'Temps verbaux — Impératif': "The singular imperative : drop the prefix and « ko- ». Ko-kende → « kende » ! For the plural, add « bo- ». To forbid : keep « ko- » and add « te » at the end.",
    'Négation': "The golden rule of Lingala : add « te » at the END of the sentence. Nazali koliya te, nakendaki te, nakokende te… It's the same rule for every tense !",

    // ── LINGALA — Unit 3 ──────────────────────────────────────────
    'Prépositions de lieu': "The preposition « na » is the most versatile (to, in, at, with). To be more specific, it combines with other words : « kati na » (inside), « likolo ya » (on top of), « nse ya » (under).",
    'Prépositions de temps et de relation': "« Sima ya » = after, « liboso ya » = before, « banda » = since. « Elongo na » (with/together) is very expressive in Lingala culture : unity is a core value !",
    'Adjectifs — Apparence et taille': "In Lingala, adjectives directly follow the noun : « mwana moke » = small child. « Kitoko » (beautiful) and « monene » (big) are among the most frequent !",
    'Adjectifs — Qualité et état': "« Malamu » (good/well) is the most used adjective in Lingala ! It also works as an all-purpose reply. « Ozali malamu ?» — « Nazali malamu !» (How are you? — I'm doing well !)",
    'Adjectifs — Caractère': "« Makasi » (strong/intense) is very expressive and is used to amplify any adjective. « Malamu makasi » = really good. « Ya sika » (new) and « ya kala » (old) work with any noun !",
    'Conjonctions': "« Mpe » (and), « kasi » (but), « soki » (if). « Atako » (although) is very expressive : « Atako azali moke, azali makasi » = Although he's small, he's strong !",
    'Adverbes': "« Mingi » (a lot/very) is the most common adverb in Lingala ! « Nalingi yo mingi » = I love you a lot. « Tango nyonso » (always) and « seko » (never) are the two extremes of frequency.",
  },
  ff: {
    // ── FULA — Unit 1 ──────────────────────────────────────────
    'Pronoms personnels (ɗon)': "In Fula, the progressive marker « miɗo/aɗa/omo » is placed between the subject and the verb. « Miɗo yaha » = I am going. It's the equivalent of the present continuous !",
    'Pronoms emphatiques': "To emphasize WHO performs the action, use the long forms : Miin (me), Aan (you), Kanko (him/her). E.g. : « Miin mi waɗii ko » = It was ME who did it. Very expressive !",
    'Pronoms objets': "Object pronouns are suffixes attached to the verb in Fula ! « Dimmal-am » = Help me, « rokki-mo » = I gave it to him/her. The suffix changes according to person (-am, -ma, -mo...).",
    'Pronoms possessifs': "Possession is marked AFTER the noun in Fula. « Galle am » = My house (lit. house my). The forms : am (my), maa (your), makko (his/her), amen (our), mooɗon (your, plural), maɓɓe (their).",
    'Pronoms sujets simples': "The simple pronouns mi/a/o/minen/onon/ɓe are mainly used in the past and imperative. « Mi yahii » = I went. In the present continuous, they're replaced by miɗo/aɗa/omo !",
    'Marqueurs de focus (Ko)': "« Ko » is the focus copula of Fula. It highlights the identity of the subject. « Ko miin mi waɗiri » = It was ME who did it. Very common in spoken language !",
    'Classes nominales': "Fula has more than 20 noun classes ! Each class has its own singular and plural : gorko/worɓe (man/men), nagge/na'i (cow/cows). It's one of the richest languages in the world in terms of noun classes !",
    'Structure de la phrase': "The basic word order in Fula is : Subject + Marker + Verb + Object. Focus changes the structure : « Ko miin » highlights the subject. The verb « woni » = to be is essential !",
    // ── FULA — Unit 2 ──────────────────────────────────────────
    'Temps verbaux — Présent': "The present in Fula distinguishes the progressive (miɗo yaha = I'm going right now) from the habitual (mi jangay = I study, habitually). The suffix « -ay » marks habitual action !",
    'Temps verbaux — Passé': "The past perfective is formed with the suffix « -ii » : mi yahii = I went. The imperfect uses « doon » (mi doon yaha). The discontinued habitual past uses « daan » (mi daan yaha = I used to go).",
    'Temps verbaux — Futur': "The future is formed by changing the verb's final vowel + a suffix : yaha (to go) → yehay (will go). In the plural, the prefix « nj- » is added : minen njehay = we will go. Conditional with « so » (if) !",
    'Temps verbaux — Impératif': "The singular imperative is formed with a special ending (-du) : Yahdu! = Go! Plural : Njahon! The prefix « hoto » forbids the action : Hoto yahdu! = Don't go !",
    'Négation': "The negation of the habitual present is formed with the suffix « -aa » after the verb : mi yahaa = I don't go. For the past, use « -iino ». For the future, add « -aa » to the future form !",
    'Questions': "The essential question words : Hommbo (who), Honto (where), Honnde (when), Honno (how), Fii hondun (why), Noy (how many). For yes/no questions, use « Ndax » at the start of the sentence !",
    // ── FULA — Unit 3 ──────────────────────────────────────────
    'Prépositions de lieu': "The basic preposition « to » indicates direction (like 'to' in English). « E » marks position. To be more specific : dow (on), ley (under), nder (inside), caggal (behind), yeeso (in front of) !",
    'Prépositions de temps et de relation': "« Caggal » = after, « yeeso » = before, « ngir » = for. « Ko » expresses accompaniment (with). « Ni » indicates comparison : « Dox ni puccu » = Run like a horse !",
    'Adjectifs — Apparence physique': "In Fula, adjectives agree with the noun class ! « Moƴƴo » (beautiful/good) and « bonngo » (bad) are the most common. « Mawɗo » (big/elder) and « pamaro » (small/young) are also used for importance !",
    'Adjectifs — Caractère': "« ɗoole » (strong/brave) and « goonga » (honest) are highly valued qualities in Fula culture. « Muɗɗo » (patient) reflects patience, a central virtue in 'pulaaku' philosophy !",
    'Adjectifs — États et sensations': "« Miɗo hebla » = I am tired. « Mi anndinɗo » = I am hungry. Physical sensations are often built with the progressive pronoun miɗo or the verb woni !",
    'Adjectifs — Quantité et jugement': "« Keewɓe » (numerous) varies according to noun class. « Seeɗa » = a little — very common in everyday speech. « Seeɗa seeɗa » (little by little) also works as an adverb of manner !",
    'Conjonctions': "« E » links two nouns (and), « kono » introduces a contrast (but), « so » a condition (if). « Ndaw » expresses cause (because). These words structure narration in Fula !",
    'Adverbes': "« Tan » (only) and « tawi » (really) are very common. « Seeɗa seeɗa » (slowly, step by step) is the Fula proverb of gradual learning. « Haapoma » = never !",
  },
  sw: {
    // ── SWAHILI — Unit 1 ──────────────────────────────────────────
    'Pronoms sujets': "In Swahili, subject pronouns are PREFIXES attached to the verb : Ni-na-soma = I (Ni) – present (na) – read (soma). Every person has its own prefix : Ni, U, A, Tu, M, Wa. Very regular !",
    'Pronoms emphatiques': "The long forms Mimi, Wewe, Yeye, Sisi, Ninyi, Wao emphasize the subject. « Mimi ndiye niliyefanya » = It was ME who did it. « Ndiye » (for people) is the focus marker !",
    'Pronoms objets': "Object pronouns are infixes inserted into the verb : Ni-na-ku-ona = I see you (Ni + na + ku + ona). The order is : subject prefix + tense + object + verb. Very compact !",
    'Pronoms possessifs': "Possession agrees with the noun class of the possessed noun ! « Yangu » for nyumba (house), « changu » for kitabu (book). The root stays the same (-angu, -ako, -ake...), only the prefix changes !",
    'Classes nominales': "Swahili has 8 noun classes ! The most important : M/Wa for people (mtu/watu). Ki/Vi for languages and objects (Kiswahili). N/N is invariable. These classes change ALL grammatical agreement !",
    'Structure de la phrase': "Swahili follows SVO order, just like French ! The difference : everything gets agglutinated into the verb. « Ni-na-soma » = I read. Noun-class agreement means the adjective agrees with the noun's class !",
    // ── SWAHILI — Unit 2 ──────────────────────────────────────────
    'Temps verbaux — Présent': "The present marker is « na » : Ninasoma = I read. The habitual tense uses « hu » with no personal prefix : Husoma = One reads, habitually. Two forms for two different nuances !",
    'Temps verbaux — Passé': "The simple past uses « li » : Nilisoma = I read (past). The perfect uses « me » : Nimesoma = I have read (and the result still holds). The imperfect combines as « nilikuwa nikisoma » = I was reading !",
    'Temps verbaux — Futur': "The future is formed with « ta » : Nitasoma = I will read. The conditional « ki » means 'if' : Nikisoma = if I read. « Nge » expresses the hypothetical : Ningesoma = I would read. Systematic and regular !",
    'Temps verbaux — Impératif': "The singular imperative = the bare verb root : Soma! = Read! The plural adds « -ni » : Someni! To forbid in the singular : Usi + verb (Usisome!). Plural : Msi + verb (Msisome!). Simple !",
    'Négation': "Negation transforms the subject prefix : Ni → Si, U → Hu, A → Ha, Tu → Hatu, M → Ham, Wa → Hawa. Sisomi = I don't read. For the past : Sikuenda = I didn't go. For the perfect : Sijasoma = I haven't read yet !",
    'Questions': "Nani (who), Nini (what), Wapi (where), Lini (when), Vipi (how), Kwa nini (why), Ngapi (how many). For a yes/no question, start with « Je » : Je, unaenda? = Are you going ?",
    // ── SWAHILI — Unit 3 ──────────────────────────────────────────
    'Prépositions de lieu': "Locative phrases are formed with « ya » : Juu ya (on), Chini ya (under), Mbele ya (in front of), Nyuma ya (behind). « Katika » = in. « Karibu na » = near, « mbali na » = far from !",
    'Prépositions de temps et de relation': "« Baada ya » = after, « kabla ya » = before, « tangu » = since, « hadi » = until. « Kwa » is the most versatile preposition : kwa daktari = at the doctor's, kwa gari = by car !",
    'Adjectifs — Apparence physique': "Adjectives agree with the noun class ! « Mzuri » for M/Wa, « nzuri » for N, « zuri » for Ji/Ma... Mkubwa (big), mdogo (small), mrefu (tall), mfupi (short). The root stays, the prefix changes !",
    'Adjectifs — Caractère': "« Mvumilivu » (patient) and « mwaminifu » (faithful) are highly valued in Swahili culture. « Mwerevu » (clever/sharp) is also used for someone crafty. « Mpole » = gentle and polite !",
    'Adjectifs — États et sensations': "Sensations are often built with « Nina » : Nina njaa = I'm hungry, Nina joto = I'm hot. « Mchoka » = tired. « Tamu » (delicious) is the ultimate compliment for Swahili food !",
    'Adjectifs — Quantité et jugement': "« Nyingi » (many) and « chache » (few) agree with noun classes. « Rahisi » (easy) also means 'cheap' ! « Sana » (very) is added after any adjective : nzuri sana = very beautiful !",
    'Conjonctions': "« Na » (and/with), « lakini » (but), « au » (or), « kwa sababu » (because). « Ingawa » (although) is used to express concession. « Kama » = if/like — one word, two functions !",
    'Adverbes': "« Sana » (very/a lot) is the most common adverb in Swahili ! « Polepole » (slowly) is also a proverb : « Haraka haraka haina baraka » = Haste brings no blessing. « Bado » = still/not yet !",
  },
  yo: {
    // ── YORUBA — Unit 1 ──────────────────────────────────────────
    "Pronoms possessifs — Le mien, le tien": "The rule: Tí (\"the one of\") + pronoun. When it joins the pronoun, Tí loses its \"í\": Tí + Èmi → t’èmi, the apostrophe marks the dropped vowel. No masculine or feminine: t’òun = his or hers. For \"yours\", t’ìwọ and t’ìrẹ mean the same. To make a sentence: … ni (it is …), … nìyẹn (that is …), … l’èyí (this is …), … kọ́ (it is not …). Examples: Ìwé yìí kì í ṣe t’èmi = This book isn't mine. Ṣé ife yìí jẹ́ t’ìrẹ? = Is this cup yours? T’àwọn ni mo mú = It's theirs that I took.",
  },
};

const getMascotTip = (
  languageCode: string,
  subcategory: string,
  language: 'fr' | 'en' = 'fr'
): string | undefined =>
  (language === 'en' ? GRAMMAR_MASCOT_TIPS_EN : GRAMMAR_MASCOT_TIPS)[languageCode]?.[subcategory];

interface CategoryDetailsProps {
  category: string;
  rules: [string, WordData][];
  completedRules: Set<string>;
  onStartLearning: () => void;
  onBack: () => void;
  onStartRevision?: () => void;
  isMobileView?: boolean;
  onPlayAudio?: (audio: string) => Promise<void>;
  onExitAttempt?: (callback: () => void) => void;
  showExitConfirmation?: boolean;
  onExitConfirm?: () => void;
  onExitCancel?: () => void;
  userProgress?: UserProgress;
  mascotTip?: string;
}

interface TestWord {
  word: string;
  data: WordData;
  correctAnswers: number;
  tested: number;
}

interface SessionResults {
  subcategory: string;
  masteredWords: TestWord[];
  date: number;
}

// ✅ SUPPRIMÉ: Fonctions saveProgress et loadProgress - maintenant centralisé

const AdaptiveBackButton: React.FC<{
  onBack: () => void;
  isMobileView?: boolean;
  title?: string;
  onExitAttempt?: (callback: () => void) => void;
}> = ({ onBack, isMobileView = false, title, onExitAttempt }) => {
  const handleBackClick = () => {
    if (onExitAttempt) {
      onExitAttempt(onBack);
    } else {
      onBack();
    }
  };

  if (isMobileView) {
    return (
      <div className="grammar-header mobile-layout">
        <FloatingBackButton onBack={handleBackClick} mode="inline" variant="minimal" />
        {title && <h2 className="grammar-mobile-title">{title}</h2>}
        <div className="grammar-header-spacer"></div>
      </div>
    );
  }

  // Desktop : bouton flottant (portal) sous le topbar fixe, sinon il est masqué par celui-ci
  return <FloatingBackButton onBack={handleBackClick} position="top-left" variant="minimal" />;
};

const hasValidAudio = (audio: string | undefined): boolean => {
  return !!audio && audio.trim() !== '';
};

const SubcategoryCard: React.FC<SubcategoryCardProps> = ({
  title,
  completed,
  onClick,
  count,
  completedCount,
  mascotTip,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const pct = count > 0 ? Math.min(100, Math.round((completedCount / count) * 100)) : 0;
  const stars = pct === 100 ? 3 : pct >= 60 ? 2 : pct >= 1 ? 1 : 0;
  const started = completedCount > 0;
  const [showMascotTip, setShowMascotTip] = useState(false);

  const text = isDark ? '#f1f5f9' : '#1e293b';
  const sub  = isDark ? '#94a3b8' : '#64748b';

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      onHoverStart={() => mascotTip && setShowMascotTip(true)}
      onHoverEnd={() => setShowMascotTip(false)}
      style={{
        borderRadius: '14px',
        padding: '14px 12px',
        cursor: 'pointer',
        background: isDark
          ? started ? 'rgba(201,169,97,0.12)' : 'rgba(255,255,255,0.04)'
          : started ? 'rgba(201,169,97,0.1)'  : 'rgba(0,0,0,0.03)',
        border: `1px solid ${pct === 100 ? '#c9a961' : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {/* Icone + nom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '22px' }}>
          {completed ? <Picto name="oui" /> : <Book size={20} color={isDark ? '#c9a961' : '#8b6914'} />}
        </span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: text, lineHeight: 1.3 }}>
          {title}
        </span>
      </div>

      {/* Stars + compteur */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', letterSpacing: '1px' }}>
          {[1, 2, 3].map(i => (
            <span key={i} style={{ opacity: i <= stars ? 1 : 0.25 }}><Picto name="etoile" /></span>
          ))}
        </span>
        <span style={{ fontSize: '11px', color: sub, fontWeight: 500 }}>
          {completedCount}/{count}
        </span>
      </div>

      {/* Barre de progression */}
      {count > 0 && (
        <div style={{ height: '4px', borderRadius: '2px', background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: pct === 100
              ? 'linear-gradient(90deg,var(--color-success),var(--color-success-dark))'
              : 'linear-gradient(90deg,#c9a961,#e8844d)',
            borderRadius: '2px',
            transition: 'width 0.6s ease',
          }} />
        </div>
      )}

      {/* Coche si complété */}
      {pct === 100 && (
        <div style={{ position: 'absolute', top: '8px', right: '8px', color: 'var(--color-success)' }}>
          <CheckCircle size={14} />
        </div>
      )}

      {/* Bulle mascotte Jogo au survol */}
      <AnimatePresence>
        {showMascotTip && mascotTip && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
              display: 'flex',
              alignItems: 'flex-end',
              gap: '8px',
              pointerEvents: 'none',
              width: 'max-content',
              maxWidth: '260px',
            }}
          >
            {/* Bulle texte */}
            <div style={{
              background: isDark ? 'var(--surface)' : '#fff',
              border: `1.5px solid #c9a961`,
              borderRadius: '12px',
              borderBottomRightRadius: '4px',
              padding: '8px 12px',
              fontSize: '12px',
              lineHeight: '1.5',
              color: isDark ? 'var(--text)' : 'var(--text)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            }}>
              {mascotTip}
            </div>
            {/* Mascotte */}
            <img
              src="/Mascotte/mascotte5.png"
              alt="Jogo"
              style={{ width: '36px', height: '36px', objectFit: 'contain', flexShrink: 0 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CategoryDetails: React.FC<CategoryDetailsProps> = ({
  category,
  rules,
  completedRules,
  onStartLearning,
  onBack,
  onStartRevision,
  isMobileView = false,
  onPlayAudio,
  onExitAttempt,
  showExitConfirmation = false,
  onExitConfirm,
  onExitCancel,
  userProgress,
  mascotTip,
}) => {
  const categoryCompletedRules = new Set(
    Array.from(completedRules).filter(rule =>
      rules.some(([currentRule]) => currentRule === rule)
    )
  );

  const remainingRules = rules.filter(([rule]) => !categoryCompletedRules.has(rule));
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const progressPct = rules.length > 0 ? Math.round((categoryCompletedRules.size / rules.length) * 100) : 0;
  const isCategoryDone = remainingRules.length === 0 && rules.length > 0;

  return (
    <>
      <div className="gram-detail-screen" data-theme={theme}>
        <TribalBorder />
        <AdaptiveBackButton
          onBack={onBack}
          isMobileView={isMobileView}
          onExitAttempt={onExitAttempt}
        />

        {/* En-tête */}
        <div className="gram-detail-topbar">
          <p className="gram-detail-topbar__sub">{t('grammar.sectionLabel')}</p>
          <h1 className="gram-detail-topbar__title">{category}</h1>
          <div className="gram-detail-progress-bar">
            <motion.div
              className="gram-detail-progress-bar__fill"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            />
          </div>
          <p className="gram-detail-topbar__count">
            {t('grammar.wordsCount', { done: categoryCompletedRules.size, total: rules.length })}
          </p>
        </div>

        {/* Bulle mascotte */}
        {mascotTip && (
          <motion.div
            className="gram-detail-mascot"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <img src="/Mascotte/mascotte5.png" alt={t('grammar.mascotAlt') as string} className="gram-detail-mascot__img" />
            <div className="gram-detail-mascot__bubble">{mascotTip}</div>
          </motion.div>
        )}

        {/* Boutons d'action */}
        <div className="gram-detail-actions">
          {isCategoryDone ? (
            <motion.button
              className="gram-detail-btn gram-detail-btn--accent"
              onClick={onBack}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('grammar.categoryDone')}
            </motion.button>
          ) : (
            <motion.button
              className="gram-detail-btn gram-detail-btn--accent"
              onClick={onStartLearning}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('grammar.startLearning', { count: remainingRules.length })}
            </motion.button>
          )}
          {categoryCompletedRules.size > 0 && !isCategoryDone && (
            <motion.button
              className="gram-detail-btn gram-detail-btn--ghost"
              onClick={onStartRevision ?? onBack}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('grammar.review', { count: categoryCompletedRules.size })}
            </motion.button>
          )}
        </div>

        {/* Liste des règles */}
        <div className="gram-detail-section">
          <div className="gram-detail-section-header">
            <span className="gram-detail-section-title">{t('grammar.wordsOfCategory')}</span>
          </div>

          <div className="gram-detail-deco-border" aria-hidden="true" />

          <div>
            {rules.map(([word, data]) => {
              const isDone = categoryCompletedRules.has(word);
              return (
                <div key={word} className={`gram-detail-row${isDone ? ' gram-detail-row--done' : ''}`}>
                  <div className="gram-detail-row__text">
                    <span className="gram-detail-row__word">{word}</span>
                    <span className="gram-detail-row__translation">{getWordTranslation(data, i18n.language)}</span>
                  </div>
                  <div className="gram-detail-row__right">
                    {isDone && <span className="gram-detail-check">✓</span>}
                    {hasValidAudio(data.audio) && (
                      <motion.button
                        className="gram-detail-audio-btn"
                        onClick={e => { e.stopPropagation(); onPlayAudio && onPlayAudio(data.audio!); }}
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={t('learnMode.listenButton') as string}
                      >
                        <Volume2 size={16} />
                      </motion.button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="gram-detail-deco-border gram-detail-deco-border--bottom" aria-hidden="true" />
        </div>
      </div>

      <ConfirmationModal
        isOpen={showExitConfirmation}
        onConfirm={onExitConfirm || (() => {})}
        onCancel={onExitCancel || (() => {})}
        message={t('learnMode.exitConfirm.message') as string}
        title={t('learnMode.exitConfirm.title') as string}
        confirmText={t('learnMode.exitConfirm.confirm') as string}
        cancelText={t('learnMode.exitConfirm.cancel') as string}
        confirmButtonStyle="danger"
        position="top"
      />
    </>
  );
};

// Fonctions utilitaires
const shuffleArray = <T extends any>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Composant principal GrammarMode
// Keywords to classify grammar subcategories by unit
const GRAMMAR_UNIT1_KEYWORDS = ['pronom', 'classe', 'structure', 'ìjíṣẹ́', 'personal', 'àkọ́kọ́', 'ìṣọ́ (possessive)'];
const GRAMMAR_UNIT2_KEYWORDS = ['temps verbaux', 'négation', 'question', 'conjugaison', 'particules verbales', 'àṣeyọrí', 'verb tense', 'ọ̀rọ̀ àṣeyọrí'];
const GRAMMAR_UNIT3_KEYWORDS = ['préposition', 'preposition', 'postposition', 'adjectif', 'adverbe', 'conjonction', 'marqueurs de focus'];

const filterGrammarByUnit = (
  cats: Record<string, any>,
  unit: number
): Record<string, any> => {
  const entries = Object.entries(cats);
  const lk = (s: string) => s.toLowerCase();
  const isU1 = (k: string) => GRAMMAR_UNIT1_KEYWORDS.some(kw => lk(k).includes(lk(kw)));
  const isU2 = (k: string) => GRAMMAR_UNIT2_KEYWORDS.some(kw => lk(k).includes(lk(kw)));
  const isU3 = (k: string) => GRAMMAR_UNIT3_KEYWORDS.some(kw => lk(k).includes(lk(kw)));
  let filtered: [string, any][];
  if (unit === 1) filtered = entries.filter(([k]) => isU1(k));
  else if (unit === 2) filtered = entries.filter(([k]) => isU2(k));
  else if (unit === 3) filtered = entries.filter(([k]) => isU3(k));
  else filtered = entries.filter(([k]) => !isU1(k) && !isU2(k) && !isU3(k));
  // Fallback: if nothing matches (e.g. language has few categories) show all
  return filtered.length > 0 ? Object.fromEntries(filtered) : cats;
};

const GrammarMode: React.FC<GrammarModeProps> = ({
  languageCode,
  onBackToCategories,
  onCategoryComplete,
  onWordsLearned,
  resetToken,
  isLoading = false,
  isMobileView,
  userProgress,
  saveProgressOnSummary,
  grammarUnit,
  grammarSubcategoryFilter,
  onAnswerSubmit,
  onStartRevision,
  onFilterComplete,
  onShare,
}) => {
  const { playWord } = useSupabaseAudio(languageCode);
  const { t, i18n } = useTranslation();

  // États existants
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [completedRules, setCompletedRules] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<'selection' | 'category-details' | 'preview' | 'consolidation' | 'test' | 'summary' | 'module-eval'>('selection');
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [mode]);

  // Stabilise le proverbe et l'encouragement — recalculés uniquement au changement de mode
  const summaryCongrats = useMemo(() => getRandomCongrats(languageCode), [mode]);
  const summaryProverb  = useMemo(() => getRandomProverb(languageCode),  [mode]);
  const [consolidationSentences, setConsolidationSentences] = useState<RevisionSentence[]>([]);
  const [consolidationWordKey, setConsolidationWordKey] = useState<string>('');
  const [consolidationWordTranslation, setConsolidationWordTranslation] = useState<string>('');
  // Mots déjà consolidés dans cette session (pour ne pas répéter)
  const consolidatedWordsRef = React.useRef<Set<string>>(new Set());
  const [sessionResults, setSessionResults] = useState<SessionResults | null>(null);
  const [learningWords, setLearningWords] = useState<[string, WordData][]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [testWords, setTestWords] = useState<TestWord[]>([]);
  const [currentTestWord, setCurrentTestWord] = useState<TestWord | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [stableOptions, setStableOptions] = useState<string[]>([]);
  const [user, setUser] = useState<any | null>(null);
  const [testQueue, setTestQueue] = useState<Array<{word: string, data: WordData}>>([]);
  const [showContinueButton, setShowContinueButton] = useState<boolean>(false);
  const { theme } = useTheme();

  // États pour la sauvegarde automatique centralisée
  const [shouldSave, setShouldSave] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [hasSavedThisSession, setHasSavedThisSession] = useState(false);

  // États pour l'animation XP
  const [showXPAnimation, setShowXPAnimation] = useState(false);
  const [xpGained, setXpGained] = useState(0);
  
  // États pour l'exit confirmation
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);
  
  // États pour la gestion du résumé en étapes
  const [summaryStep, setSummaryStep] = useState<'results' | 'xp'>('results');

  const currentTestWordRef = useRef<TestWord | null>(null);
  const testQueueRef = useRef<Array<{word: string, data: WordData}>>([]);
  const selectedSubcategoryRef = useRef<string | null>(null);
  const testWordsRef = useRef<TestWord[]>([]);

  const { playSummaryAudio } = useSummaryAudio();

  useEffect(() => {
    currentTestWordRef.current = currentTestWord;
  }, [currentTestWord]);

  useEffect(() => {
    testQueueRef.current = testQueue;
  }, [testQueue]);

  useEffect(() => {
    selectedSubcategoryRef.current = selectedSubcategory;
  }, [selectedSubcategory]);

  useEffect(() => {
    testWordsRef.current = testWords;
  }, [testWords]);
  
  // Effets existants
  useEffect(() => {
    const token = localStorage.getItem('googleToken');
    if (token) {
      setUser({ token });
    }
  }, []);

  const languageData = useMemo(() => {
    try {
      return getLanguageData(languageCode);
    } catch (error) {
return null;
    }
  }, [languageCode]);

  const grammarCategories = useMemo(() => {
    if (!languageData?.categories) return {};
    try {
      return (languageData.categories['Grammaire'] || {}) as GrammarCategory;
    } catch (error) {
return {};
    }
  }, [languageData]);

  const displayedGrammarCategories = useMemo(() => {
    let filtered = grammarUnit ? filterGrammarByUnit(grammarCategories, grammarUnit) : grammarCategories;
    if (grammarSubcategoryFilter && grammarSubcategoryFilter.length > 0) {
      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([k]) => grammarSubcategoryFilter.includes(k))
      );
    }
    return filtered;
  }, [grammarCategories, grammarUnit, grammarSubcategoryFilter]);

  const currentSubcategoryRules = useMemo(() => {
    if (!selectedSubcategory || !grammarCategories[selectedSubcategory]) return [];
    
    return Object.entries(grammarCategories[selectedSubcategory])
      .filter(([key]) => !key.includes('_'))
      .map(([key, value]) => {
        const grammarItem = value as GrammarItem;
        return [key, {
          translation: grammarItem.translation || grammarItem.fr || '',
          explanation: grammarItem.explanation || grammarItem.desc || '',
          example: grammarItem.example || grammarItem.examples?.[0] || '',
          audio: grammarItem.audio || undefined
        }] as [string, WordData];
      });
  }, [selectedSubcategory, grammarCategories]);
  
  // ✅ NOUVEAU : Chargement initial centralisé (localStorage seulement)
 useEffect(() => {
    const initializeProgress = async () => {

    

      const savedRules = new Set<string>();
      
      // 1. PRIORITÉ: userProgress.recentlyLearnedWords
      if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
     
        
        userProgress.recentlyLearnedWords.forEach(item => {
          if (item.category === 'Grammaire') {
            savedRules.add(item.word);
          }
        });
      
      }
      
      // 2. FALLBACK: localStorage
      const storageKey = `grammar-progress-${languageCode}`;
      const savedProgress = localStorage.getItem(storageKey);
      
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          progress.forEach((session: SessionResults) => {
            session.masteredWords.forEach((word: TestWord) => {
              savedRules.add(word.word);
            });
          });
        
        } catch (error) {
}
      }
      
      setCompletedRules(savedRules);
      if (modeRef.current === 'selection') {
        window.scrollTo(0, 0);
      }
    };

    initializeProgress();
  }, [languageCode, resetToken, userProgress]);



  // Fonction stable pour générer les mauvaises réponses. correctAnswer doit
  // déjà être la valeur localisée (voir prepareOptionsForWord) pour que le
  // filtre d'exclusion et le pool de distracteurs restent cohérents avec elle.
  const generateWrongAnswers = useCallback((correctAnswer: string, allRules: [string, WordData][]): string[] => {
    const otherTranslations = allRules
      .map(([, data]) => getWordTranslation(data, i18n.language))
      .filter(translation => translation && translation !== correctAnswer);
    return shuffleArray(otherTranslations).slice(0, 3);
  }, [i18n.language]);

  // Fonction stable pour préparer les options
  const prepareOptionsForWord = useCallback((word: TestWord) => {
    if (!word?.data?.translation) {
return;
    }

    const correctAnswer = getWordTranslation(word.data, i18n.language);
    const options = [
      correctAnswer,
      ...generateWrongAnswers(correctAnswer, currentSubcategoryRules)
    ].filter(Boolean);
    
    if (options.length >= 4) {
      const shuffledOptions = shuffleArray(options);
     
      setStableOptions(shuffledOptions);
    } else {
}
  }, [generateWrongAnswers, currentSubcategoryRules, i18n.language]);

  // handleContinue sans dépendances
  const handleContinue = useCallback((updatedScores?: TestWord[]) => {
 
    const currentWord = currentTestWordRef.current;
    const currentQueue = testQueueRef.current;
    const currentSubcat = selectedSubcategoryRef.current;
    const currentTestWords = testWordsRef.current;
    
    if (!currentWord) {
return;
    }
    
    if (currentQueue.length === 0) {
return;
    }
    
    // Nettoyer l'UI immédiatement
    setSelectedAnswer(null);
    setFeedback(null);
    setShowContinueButton(false);
    
    // Traiter la queue
    const newQueue = currentQueue.slice(1);
   
    
    // Vérifier fin de test AVANT mise à jour
    if (newQueue.length === 0) {


      setTestQueue([]);
      setCurrentTestWord(null);
      setStableOptions([]);

      const finalScores = updatedScores || currentTestWords;
      const masteredWords = finalScores.filter((w: TestWord) => w.correctAnswers >= 3);

      setSessionResults({
        subcategory: currentSubcat || '',
        masteredWords,
        date: Date.now()
      });
      setHasSavedThisSession(false);
      setShouldSave(true);

      // Consolidation de fin de session : priorité aux mots non maîtrisés
      if (languageData?.sentencesGap) {
        const allSentences: RevisionSentence[] = [];
        // Trier : non maîtrisés d'abord, puis maîtrisés
        const sorted = [...finalScores].sort((a, b) => {
          const aOk = a.correctAnswers >= 3 ? 1 : 0;
          const bOk = b.correctAnswers >= 3 ? 1 : 0;
          return aOk - bOk;
        });
        for (const tw of sorted) {
          const sents = getSentencesForWord(languageData.sentencesGap, tw.word);
          // Ne garder que les phrases dont le testWord correspond au mot de grammaire
          const filtered = sents.filter(
            s => s.testWord && s.testWord.toLowerCase() === tw.word.toLowerCase()
          );
          allSentences.push(...filtered);
        }
        if (allSentences.length > 0) {
          setConsolidationSentences(allSentences);
          setConsolidationWordKey('');
          setConsolidationWordTranslation('');
          setMode('consolidation');
          return;
        }
      }

      setMode('summary');
      return;
    }
    
    // Préparer la question suivante
    setTestQueue(newQueue);
    
    const nextTest = newQueue[0];
    const finalScores = updatedScores || currentTestWords;
    const nextWordStats = finalScores.find((w: TestWord) => w.word === nextTest.word) || {
      word: nextTest.word,
      data: nextTest.data,
      correctAnswers: 0,
      tested: 0
    };
    
  
    
    // Préparer les options AVANT de définir le mot
    const nextCorrectAnswer = getWordTranslation(nextWordStats.data, i18n.language);
    const options = [
      nextCorrectAnswer,
      ...generateWrongAnswers(nextCorrectAnswer, currentSubcategoryRules)
    ].filter(Boolean);
    
    if (options.length >= 4) {
      const shuffledOptions = shuffleArray(options);
    
      
      setStableOptions(shuffledOptions);
      setCurrentTestWord(nextWordStats);
      
    
    } else {
if (newQueue.length > 1) {
        setTimeout(() => handleContinue(updatedScores), 50);
      }
    }
    
  }, [generateWrongAnswers, currentSubcategoryRules, languageData, i18n.language]);

  // handleAnswer simplifié et typé correctement
  const handleAnswer = useCallback(async (answer: string) => {
    const currentWord = currentTestWord;
    const currentTestWords = testWords;

    if (!currentWord?.data?.translation) return;

    // Doit correspondre à la valeur localisée distribuée comme bonne réponse
    // par prepareOptionsForWord/handleContinue (voir generateWrongAnswers).
    const correctAnswer = getWordTranslation(currentWord.data, i18n.language);
    const isCorrect = answer === correctAnswer;
    setSelectedAnswer(answer);
    setFeedback(isCorrect ? t('grammar.correct') : t('grammar.correctAnswerIs', { answer: correctAnswer }));
    onAnswerSubmit?.(isCorrect);

    // Audio
    if (currentWord.data.audio) {
      try {
        await playWord(currentWord.data.audio);
      } catch (error) {
}
    }

    // Mise à jour scores
    const updatedScores = currentTestWords.map((word: TestWord) => 
      word.word === currentWord.word
        ? {
            ...word,
            correctAnswers: isCorrect ? word.correctAnswers + 1 : word.correctAnswers,
            tested: word.tested + 1
          }
        : word
    );
    
    setTestWords(updatedScores);
    
    if (isCorrect) {
      setTimeout(() => {
        handleContinue(updatedScores);
      }, 800);
    } else {
      setShowContinueButton(true);
    }

  }, [currentTestWord, testWords, playWord, handleContinue, languageData, i18n.language, t]);

  // Bouton continuer simplifié
  const handleContinueButtonClick = useCallback(() => {
  
    handleContinue();
  }, [handleContinue]);

  const handleStartLearning = useCallback(() => {
    if (!currentSubcategoryRules?.length) return;
    const uncompletedRules = currentSubcategoryRules.filter(([rule]) => !completedRules.has(rule));
    if (uncompletedRules.length > 0) {
      const selectedRules = uncompletedRules.slice(0, 5);
      setLearningWords(selectedRules);
      setTestWords(selectedRules.map(([word, data]) => ({
        word,
        data,
        correctAnswers: 0,
        tested: 0
      })));
      consolidatedWordsRef.current = new Set(); // réinitialiser pour la nouvelle session
      setPreviewIndex(0);
      setMode('preview');
      setHasSavedThisSession(false);
    }
  }, [currentSubcategoryRules, completedRules]);

  const startTestPhase = useCallback(() => {
    const testQueueItems: Array<{word: string, data: WordData}> = [];
    learningWords.forEach(([word, data]) => {
      for (let i = 0; i < 3; i++) {
        testQueueItems.push({ word, data });
      }
    });
    const shuffledTestQueue = shuffleArray(testQueueItems);

    setTestWords(learningWords.map(([word, data]) => ({
      word,
      data,
      correctAnswers: 0,
      tested: 0,
    })));

    if (shuffledTestQueue.length > 0) {
      const firstTest = shuffledTestQueue[0];
      const firstTestWord = {
        word: firstTest.word,
        data: firstTest.data,
        correctAnswers: 0,
        tested: 0,
      };
      const firstCorrectAnswer = getWordTranslation(firstTestWord.data, i18n.language);
      const options = [
        firstCorrectAnswer,
        ...generateWrongAnswers(firstCorrectAnswer, currentSubcategoryRules),
      ].filter(Boolean);

      if (options.length >= 4) {
        setTestQueue(shuffledTestQueue);
        setStableOptions(shuffleArray(options));
        setCurrentTestWord(firstTestWord);
        setMode('test');
      }
    }
  }, [learningWords, generateWrongAnswers, currentSubcategoryRules, i18n.language]);

  const handleNextPreview = useCallback(() => {
    if (!learningWords?.length) return;
    if (previewIndex < learningWords.length - 1) {
      setPreviewIndex(prev => prev + 1);
    } else {
      startTestPhase();
    }
  }, [previewIndex, learningWords, startTestPhase]);

  const handleConsolidationComplete = useCallback(() => {
    setConsolidationSentences([]);
    setConsolidationWordKey('');
    setConsolidationWordTranslation('');
    setMode('summary');
  }, []);

  // ✅ SAUVEGARDE CENTRALISÉE - Version corrigée
const handleGrammarSummaryComplete = useCallback(async () => {
  if (hasSavedThisSession) {
    return;
  }
  
  try {
    const masteredWords = testWords.filter(w => w.correctAnswers >= 3);
    
    if (masteredWords.length > 0) {
      const totalXPGained = masteredWords.length * XP_PER_GRAMMAR_RULE;
      
      // ✅ 1. Préparer les mots pour la révision
      const wordsToAdd = masteredWords.map(word => ({
        word: word.word,
        category: 'Grammaire',
        subcategory: selectedSubcategory || '',
        language: languageCode,
        timestamp: Date.now(),
        translation: word.data.translation,
        explanation: word.data.explanation,
        example: word.data.example,
        audio: word.data.audio
      }));

      playSummaryAudio();
      
      // ✅ 2. Envoyer vers la révision
      if (onWordsLearned) {
        onWordsLearned(wordsToAdd);
      }
      
      // ✅ 3. Préparer localStorage grammaire
      const storageKey = `grammar-progress-${languageCode}`;
      const existingProgress = localStorage.getItem(storageKey);
      const currentProgress = existingProgress ? JSON.parse(existingProgress) : [];
      
      const newGrammarSession = {
        subcategory: selectedSubcategory || '',
        masteredWords: masteredWords.map(word => ({
          word: word.word,
          data: {
            translation: word.data.translation,
            explanation: word.data.explanation,
            example: word.data.example,
            audio: word.data.audio
          },
          correctAnswers: word.correctAnswers,
          tested: word.tested
        })),
        date: Date.now(),
        xpGained: totalXPGained
      };
      
      const updatedGrammarProgress = [...currentProgress, newGrammarSession];
      
      // ✅ 4. FUSIONNER avec userProgress existant (ne pas écraser)
      const grammarProgress: UserProgress = {
        learnedWords: new Set([
          ...Array.from(userProgress?.learnedWords || []), // ✅ GARDER existants
          ...masteredWords.map(w => w.word) // ✅ AJOUTER nouveaux
        ]),
        wordsToReview: new Set([
          ...Array.from(userProgress?.wordsToReview || []) // ✅ GARDER existants
        ]),
        wordProgress: {
          ...userProgress?.wordProgress || {} // ✅ GARDER existants
        },
        recentlyLearnedWords: [
          ...(userProgress?.recentlyLearnedWords || []), // ✅ GARDER existants
          ...wordsToAdd // ✅ AJOUTER nouveaux
        ],
        language: languageCode
      };
      
      const contextData = {
        mode: 'grammar',
        sessionStats: {
          rulesLearned: masteredWords.length,
          totalXP: totalXPGained,
          subcategory: selectedSubcategory
        },
        description: `Session grammaire ${selectedSubcategory} - ${masteredWords.length} règles maîtrisées (+${totalXPGained} XP)`,
        includeGoogleDrive: true,
        grammarSpecificData: updatedGrammarProgress
      };
      
      // ✅ 5. Sauvegarde centralisée
      if (!saveProgressOnSummary) {
        throw new Error('saveProgressOnSummary function not available');
      }
      
      await saveProgressOnSummary(grammarProgress, contextData);
      
      // ✅ 6. Mise à jour état local
      setCompletedRules(prev => {
        const newRules = new Set(prev);
        masteredWords.forEach(word => newRules.add(word.word));
        return newRules;
      });
      
      // ✅ 7. Sauvegarde localStorage backup
      localStorage.setItem(storageKey, JSON.stringify(updatedGrammarProgress));
      
      // ✅ 8. Animation XP
      if (totalXPGained > 0) {
        setXpGained(totalXPGained);
      }
    }
    
    setSaveCompleted(true);
    setHasSavedThisSession(true);
    
  } catch (error) {
setHasSavedThisSession(false);
    setSaveCompleted(false);
    alert(t('grammar.saveError', { error: error instanceof Error ? error.message : String(error) }));
  }
}, [
  testWords,
  selectedSubcategory,
  languageCode,
  onWordsLearned,
  saveProgressOnSummary,
  hasSavedThisSession,
  userProgress, // ✅ AJOUTÉ
  XP_PER_GRAMMAR_RULE
]);

useEffect(() => {
  const handleGrammarProgressSynced = (event: CustomEvent) => {
    const { sessions, language, newSessionsCount } = event.detail;
    
    if (language === languageCode) {
    
      
      // Recalculer completedRules
      const allCompletedRules = new Set<string>();
      
      sessions.forEach((session: any) => {
        if (session.masteredWords && Array.isArray(session.masteredWords)) {
          session.masteredWords.forEach((wordData: any) => {
            if (wordData.word) {
              allCompletedRules.add(wordData.word);
            }
          });
        }
      });
      
      
      setCompletedRules(allCompletedRules);
    }
  };

  window.addEventListener('grammarProgressSynced', handleGrammarProgressSynced as EventListener);
  
  return () => {
    window.removeEventListener('grammarProgressSynced', handleGrammarProgressSynced as EventListener);
  };
}, [languageCode]);

  useEffect(() => {
    if (shouldSave && mode === 'summary' && !saveCompleted && !hasSavedThisSession) {
    
      
      setHasSavedThisSession(true);
      
      const performSave = async () => {
        try {
          await handleGrammarSummaryComplete();
          setShouldSave(false);
        } catch (error) {
setHasSavedThisSession(false);
        }
      };
      
      performSave();
    }
  }, [shouldSave, mode, saveCompleted, hasSavedThisSession, handleGrammarSummaryComplete]);

  useEffect(() => {
    if (mode === 'summary' && summaryStep === 'results') {
     
      
      const timer = setTimeout(() => {
      
        setShowContinueButton(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      setShowContinueButton(false);
    }
  }, [mode, summaryStep]);

  // Logique d'exit simplifiée
 // Logique d'exit simplifiée
const handleExitAttempt = useCallback((exitCallback: () => void) => {
 
  if (showExitConfirmation) {
   
    return;
  }
  
  // ✅ CORRECTION: Ne demander confirmation QUE pendant preview et test
  // Ne PAS demander confirmation sur category-details car c'est juste la vue de la liste
  const hasActiveSession = (
    (mode === 'preview' && learningWords.length > 0) ||
    (mode === 'consolidation' && learningWords.length > 0) ||
    (mode === 'test' && (currentTestWord !== null || testQueue.length > 0))
  );
  
  if (hasActiveSession) {
   
    setPendingExit(() => exitCallback);
    setShowExitConfirmation(true);
    
  } else {
  
    exitCallback();
  }
}, [mode, currentTestWord, testQueue.length, learningWords.length, showExitConfirmation]);

  const cleanupState = useCallback(() => {
   
    setCurrentTestWord(null);
    setTestQueue([]);
    setLearningWords([]);
    setPreviewIndex(0);
    setSelectedAnswer(null);
    setFeedback(null);
    setShowContinueButton(false);
    setStableOptions([]);
    setTestWords([]);
    setShowExitConfirmation(false);
    setPendingExit(null);
  }, []);

 

  const handleExitConfirm = useCallback(() => {
    
    
    setShowExitConfirmation(false);
    const callbackToExecute = pendingExit;
    setPendingExit(null);
    
    if (callbackToExecute) {
    
      try {
        callbackToExecute();
      } catch (error) {
}
    } else {
}
  }, [pendingExit]);

  const handleExitCancel = useCallback(() => {
  
    setShowExitConfirmation(false);
    setPendingExit(null);
  }, []);

  const handleShowXP = useCallback(() => {
    
    
    if (xpGained > 0) {
      setSummaryStep('xp');
      
      setTimeout(() => {
        setShowXPAnimation(true);
      }, 300);
    } else {
      setSummaryStep('xp');
    }
  }, [xpGained]);

  const resetSummaryState = useCallback(() => {
    setSummaryStep('results');
    setShowXPAnimation(false);
    setXpGained(0);
    setShouldSave(false);
    setSaveCompleted(false);
    setShowContinueButton(false);
    setHasSavedThisSession(false);
  }, []);
  
 const handlePlayAudio = useCallback(async (audioSrc: string) => {
  try {
    // Extraire le mot du chemin audio (si c'est un chemin)
    const word = audioSrc.includes('/') 
      ? audioSrc.split('/').pop()?.replace(/\.(mp3|wav|ogg)$/, '') || audioSrc
      : audioSrc;
    
    const success = await playWord(word);
    if (!success) {
     
    }
  } catch (error) {
}
}, [playWord]);

  if (isLoading) {
    return (
      <div className="component-wrapper">
        <div className="grammar-main-container card" data-theme={theme}>
          <div className="loading-overlay">{t('grammar.loading')}</div>
        </div>
      </div>
    );
  }

  if (!languageData) {
    return (
      <div className="component-wrapper">
        <div className="grammar-main-container card" data-theme={theme}>
          <div className={`grammar-error ${theme}`}>{t('grammar.loadError')}</div>
        </div>
      </div>
    );
  }

  if (mode === 'category-details' && selectedSubcategory) {
    return (
      <CategoryDetails
        category={selectedSubcategory}
        rules={currentSubcategoryRules}
        completedRules={completedRules}
        onStartLearning={handleStartLearning}
        onBack={() => {
          setMode('selection');
          setSelectedSubcategory(null);
        }}
        onStartRevision={onStartRevision}
        isMobileView={isMobileView}
        onPlayAudio={handlePlayAudio}
        onExitAttempt={handleExitAttempt}
        showExitConfirmation={showExitConfirmation}
        onExitConfirm={handleExitConfirm}
        onExitCancel={handleExitCancel}
        mascotTip={getMascotTip(languageCode, selectedSubcategory, i18n.language === 'en' ? 'en' : 'fr')}
      />
    );
  }

  if (mode === 'preview' && selectedSubcategory && learningWords.length > 0) {
    const currentWord = learningWords[previewIndex];
    return (
      <>
        <div className="grammar-lm-screen" data-theme={theme}>
          <AdaptiveBackButton
            onBack={() => handleExitAttempt(() => {
              setMode('category-details');
              setLearningWords([]);
            })}
            isMobileView={isMobileView}
            title={isMobileView ? selectedSubcategory : undefined}
            onExitAttempt={handleExitAttempt}
          />
          <div className="lm-test">
            <div className="lm-arch-card">
              <div className="lm-arch-card__dome">
                <p className="lm-arch-card__label">{t('grammar.learnWord')}</p>
                <h2 className="lm-arch-card__word">{currentWord[0]}</h2>
                {hasValidAudio(currentWord[1].audio) && (
                  <div className="lm-audio-wrap">
                    <motion.button
                      className="lm-audio-btn"
                      onClick={() => playWord(currentWord[1].audio!)}
                      whileTap={{ scale: 0.93 }}
                    >
                      <Volume2 size={20} />
                    </motion.button>
                    <span className="lm-audio-label">{t('learnMode.listenButton')}</span>
                  </div>
                )}
              </div>
              <div className="lm-preview-translation">
                <p className="lm-preview-translation__text">{getWordTranslation(currentWord[1], i18n.language)}</p>
              </div>
            </div>

            {currentWord[1].explanation && (
              <div className="lm-usage-box">
                <div className="lm-usage-box__label">{t('learnMode.usageLabel')}</div>
                <p className="lm-usage-box__text">{getWordExplanation(currentWord[1], i18n.language)}</p>
              </div>
            )}

            {currentWord[1].example && (
              <div className="lm-context-box">
                <div className="lm-context-box__label">{t('grammar.inContext')}</div>
                <p className="lm-context-box__quote">« {getWordExample(currentWord[1], i18n.language)} »</p>
              </div>
            )}

            <div className="lm-preview-actions">
              <button
                onClick={handleNextPreview}
                className={previewIndex === learningWords.length - 1 ? 'lm-next-btn lm-next-btn--start' : 'lm-next-btn'}
              >
                {previewIndex === learningWords.length - 1 ? (
                  <><CheckCircle size={16} /> {t('learnMode.startTest')}</>
                ) : (
                  <>{t('learnMode.nextWord')} <ChevronRight size={16} /></>
                )}
              </button>
            </div>
          </div>
        </div>

        <ConfirmationModal
          isOpen={showExitConfirmation}
          onConfirm={handleExitConfirm}
          onCancel={handleExitCancel}
          message={t('learnMode.exitConfirm.message') as string}
          title={t('learnMode.exitConfirm.title') as string}
          confirmText={t('learnMode.exitConfirm.confirm') as string}
          cancelText={t('learnMode.exitConfirm.cancel') as string}
          confirmButtonStyle="danger"
          position="top"
        />
      </>
    );
  }

  if (mode === 'consolidation' && consolidationSentences.length > 0) {
    return (
      <GrammarConsolidation
        sentences={consolidationSentences}
        wordLearned={consolidationWordKey}
        wordTranslation={consolidationWordTranslation}
        languageCode={languageCode}
        onComplete={handleConsolidationComplete}
        onBack={() => {
          setConsolidationSentences([]);
          setConsolidationWordKey('');
          setConsolidationWordTranslation('');
          setMode('summary');
        }}
        isMobileView={isMobileView}
      />
    );
  }

  if (mode === 'test' && currentTestWord && stableOptions.length === 4) {
    return (
      <>
        <div className="grammar-lm-screen" data-theme={theme}>
          <AdaptiveBackButton
            onBack={() => handleExitAttempt(() => {
              setTestWords([]);
              setTestQueue([]);
              setLearningWords([]);
              setCurrentTestWord(null);
              setSelectedAnswer(null);
              setFeedback(null);
              setStableOptions([]);
              setPreviewIndex(0);
              setSelectedSubcategory(null);
              setMode('selection');
            })}
            onExitAttempt={handleExitAttempt}
            isMobileView={isMobileView}
            title={isMobileView ? t('grammar.testTitle', { subcategory: selectedSubcategory }) as string : undefined}
          />
          <div className="lm-test">
            <div className="lm-arch-card">
              <div className="lm-arch-card__dome">
                <p className="lm-arch-card__label">{t('grammar.whatIsTranslation')}</p>
                <h2 className="lm-arch-card__word">{currentTestWord.word}</h2>
                {hasValidAudio(currentTestWord.data.audio) && (
                  <div className="lm-audio-wrap">
                    <motion.button
                      className="lm-audio-btn"
                      onClick={() => playWord(currentTestWord.data.audio!)}
                      whileTap={{ scale: 0.93 }}
                    >
                      <Volume2 size={20} />
                    </motion.button>
                    <span className="lm-audio-label">{t('learnMode.listenButton')}</span>
                  </div>
                )}
              </div>
            </div>

            <div key={`test-${currentTestWord.word}`} className="lm-options">
              {stableOptions.map((option, index) => (
                <motion.button
                  key={`${currentTestWord.word}-${option}-${index}`}
                  onClick={() => handleAnswer(option)}
                  disabled={!!selectedAnswer}
                  className={`lm-option ${
                    selectedAnswer === option
                      ? option === getWordTranslation(currentTestWord.data, i18n.language) ? 'lm-option--correct' : 'lm-option--wrong'
                      : ''
                  }`}
                  whileHover={!selectedAnswer ? { scale: 1.02 } : undefined}
                  whileTap={!selectedAnswer ? { scale: 0.97 } : undefined}
                >
                  <span className="lm-option__text">{option}</span>
                </motion.button>
              ))}
            </div>

            <AnimatePresence>
              {feedback && (
                <motion.div
                  key={`feedback-${currentTestWord?.word}`}
                  className={`lm-correction-panel ${feedback.startsWith('Correct') ? 'lm-correction-panel--correct' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <span className="lm-correction-panel__label">
                    {feedback.startsWith('Correct') ? t('grammar.correct') : t('grammar.goodAnswer')}
                  </span>
                  <p className="lm-correction-panel__answer">{getWordTranslation(currentTestWord.data, i18n.language)}</p>
                  {!feedback.startsWith('Correct') && currentTestWord.data.example && (
                    <p className="grammar-lm-example">{getWordExample(currentTestWord.data, i18n.language)}</p>
                  )}
                  {showContinueButton && (
                    <div className="lm-correction-panel__actions">
                      <motion.button
                        onClick={handleContinueButtonClick}
                        className="lm-next-btn"
                        whileTap={{ scale: 0.97 }}
                      >
                        {t('grammar.continue')} <ArrowRight size={15} />
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <ConfirmationModal
          isOpen={showExitConfirmation}
          onConfirm={handleExitConfirm}
          onCancel={handleExitCancel}
          message={t('learnMode.exitConfirm.message') as string}
          title={t('learnMode.exitConfirm.title') as string}
          confirmText={t('learnMode.exitConfirm.confirm') as string}
          cancelText={t('learnMode.exitConfirm.cancel') as string}
          confirmButtonStyle="danger"
          position="top"
        />
      </>
    );
  }

  if (mode === 'test') {
   
    
    if (!currentTestWord) {
}
    if (stableOptions.length !== 4) {
}
  }

  if (mode === 'summary' && sessionResults) {
    const masteredWordsCount = sessionResults.masteredWords.length;
    const totalXPGained = masteredWordsCount * XP_PER_GRAMMAR_RULE;
    const totalRules = currentSubcategoryRules.length;
    const congrats = summaryCongrats;
    const proverb  = summaryProverb;

    const goBack = () => {
      setMode('selection');
      setLearningWords([]);
      setTestWords([]);
      setCurrentTestWord(null);
      setSessionResults(null);
      resetSummaryState();
    };

    const hasContinue = currentSubcategoryRules.some(([rule]) => !completedRules.has(rule));

    return (
      <SummaryScreen
        theme={theme}
        label={t('grammar.summary.label')}
        congrats={congrats}
        stats={[
          { value: `+${totalXPGained}`, label: t('learnMode.summary.xp') },
          { value: `${masteredWordsCount}/${totalRules}`, label: t('grammar.summary.rules') },
          { value: selectedSubcategory?.split(' ')[0] ?? '–', label: t('grammar.summary.module') },
        ]}
        proverb={proverb}
        primaryBtn={hasContinue
          ? { label: t('learnMode.summary.continueLearning'), onClick: () => { setSessionResults(null); setMode('category-details'); resetSummaryState(); } }
          : { label: t('grammar.summary.backToTrail'), onClick: goBack }
        }
        secondaryBtn={hasContinue ? { label: t('grammar.summary.backToTrail'), onClick: goBack } : undefined}
        onBack={goBack}
        onShare={onShare}
      >
        {masteredWordsCount > 0 && (
          <div className="summ-word-section">
            <p className="summ-word-section__title">{t('learnMode.summary.masteredCount', { count: masteredWordsCount })}</p>
            {sessionResults.masteredWords.map((w, i) => (
              <div key={i} className="summ-word-row">
                <span className="summ-word-row__word">{w.word}</span>
                <span className="summ-word-row__translation">{getWordTranslation(w.data, i18n.language)}</span>
                <span className="summ-word-row__badge summ-word-row__badge--ok">✓</span>
              </div>
            ))}
          </div>
        )}
      </SummaryScreen>
    );
  }

  if (mode === 'module-eval') {
    const unitLabel = grammarUnit ? t('grammar.unitLabel', { unit: grammarUnit }) : t('grammar.fullModule');
    return (
      <GrammarModuleEval
        grammarCategories={displayedGrammarCategories as GrammarCategory}
        completedRules={completedRules}
        unitName={unitLabel}
        languageCode={languageCode}
        onComplete={() => setMode('selection')}
        onBack={() => setMode('selection')}
        isMobileView={isMobileView}
      />
    );
  }

  // Vue principale — sentier de grammaire
  const categoryEntries = Object.entries(displayedGrammarCategories);

  const gramTrailSteps = categoryEntries
    .filter(([, content]) => typeof content === 'object' && content)
    .map(([subcategory, content]) => {
      const rules = Object.entries(content as Record<string, unknown>).filter(([key]) => !key.includes('_'));
      const completedCount = rules.filter(([rule]) => completedRules.has(rule)).length;
      const total = rules.length;
      const status: 'done' | 'in_progress' | 'available' =
        completedCount === total && total > 0 ? 'done' :
        completedCount > 0 ? 'in_progress' : 'available';
      return { id: subcategory, label: subcategory, status, completedCount, total };
    });

  const gramContinueStep =
    gramTrailSteps.find(s => s.status === 'in_progress') ??
    gramTrailSteps.find(s => s.status === 'available');

  const gramDoneCount = gramTrailSteps.filter(s => s.status === 'done').length;

  return (
    <>
      <div className="gram-sentier-screen" data-theme={theme}>
        <AdaptiveBackButton
          onBack={onBackToCategories}
          onExitAttempt={handleExitAttempt}
          isMobileView={isMobileView}
          title={undefined}
        />

        {/* En-tête */}
        <div className="gram-sentier-header">
          <p className="gram-sentier-header__sub">{t('grammar.sectionLabel')}</p>
          <h1 className="gram-sentier-header__title">{languageData.name}</h1>
          <p className="gram-sentier-header__stats">
            {t('grammar.modulesStats', { count: gramTrailSteps.length, done: gramDoneCount })}
          </p>
        </div>

        {/* Arches décoratives */}
        <div className="gram-sentier-arches" aria-hidden="true" />

        {/* Sentier */}
        <div className="gram-sentier-trail">
          {gramTrailSteps.map((step, i) => {
            const isLeft    = i % 2 === 0;
            const isLast    = i === gramTrailSteps.length - 1;
            const isContinue = step.id === gramContinueStep?.id;

            let nodeClass = 'gram-sentier-node';
            if (step.status === 'done')       nodeClass += ' gram-sentier-node--done';
            else if (isContinue)              nodeClass += ' gram-sentier-node--active';
            else if (step.status === 'in_progress') nodeClass += ' gram-sentier-node--progress';
            else                              nodeClass += ' gram-sentier-node--available';

            const subLabel = step.completedCount > 0
              ? `${step.completedCount}/${step.total}${step.status === 'in_progress' ? t('grammar.continuing') : ''}`
              : t('grammar.wordsSuffix', { count: step.total });

            return (
              <div
                key={step.id}
                className={`gram-sentier-step ${isLeft ? 'gram-sentier-step--left' : 'gram-sentier-step--right'}`}
              >
                {!isLast && (
                  <div className={`gram-sentier-conn ${isLeft ? 'gram-sentier-conn--lr' : 'gram-sentier-conn--rl'}`} />
                )}
                <motion.button
                  className={nodeClass}
                  onClick={() => { setSelectedSubcategory(step.id); setMode('category-details'); }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.93 }}
                  tabIndex={-1}
                >
                  {step.status === 'done'
                    ? <span>✓</span>
                    : isContinue
                      ? <span className="gram-sentier-play-icon">▶</span>
                      : null}
                </motion.button>
                <div className="gram-sentier-label">
                  <p className="gram-sentier-label__name">{step.label}</p>
                  <p className="gram-sentier-label__sub">{subLabel}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bouton évaluation du module — toujours visible */}
        {(() => {
          const allModulesDone = gramTrailSteps.length > 0 && gramTrailSteps.every(s => s.status === 'done');
          const locked = !allModulesDone;

          return (
            <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '2px',
                height: '32px',
                background: locked
                  ? `linear-gradient(to bottom, ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}, transparent)`
                  : 'linear-gradient(to bottom, var(--gold-primary), transparent)',
                margin: '0 auto',
              }} />
              <motion.button
                onClick={() => !locked && setMode('module-eval')}
                whileHover={!locked ? { scale: 1.03, y: -2 } : undefined}
                whileTap={!locked ? { scale: 0.97 } : undefined}
                style={{
                  width: '100%',
                  maxWidth: '340px',
                  padding: '14px 20px',
                  borderRadius: '14px',
                  border: `2px solid ${locked ? (theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)') : 'var(--gold-primary)'}`,
                  background: locked
                    ? (theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')
                    : 'linear-gradient(135deg, rgba(201,169,97,0.15), rgba(232,132,77,0.1))',
                  color: locked ? (theme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)') : 'var(--gold-primary)',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  letterSpacing: '0.02em',
                }}
              >
                <span style={{ fontSize: '1.1rem' }}><EmojiText text={locked ? '🔒' : '📝'} /></span>
                {t('grammar.moduleEval')}
                {locked && (
                  <span style={{ fontSize: '0.78rem', fontWeight: 400, opacity: 0.8 }}>
                    {t('grammar.finishAllSteps')}
                  </span>
                )}
              </motion.button>

              {/* Étape suivante — visible uniquement quand tout est terminé et callback fourni */}
              {allModulesDone && onFilterComplete && (
                <motion.button
                  onClick={onFilterComplete}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    width: '100%',
                    maxWidth: '340px',
                    padding: '14px 20px',
                    borderRadius: '14px',
                    border: '2px solid var(--color-primary)',
                    background: 'var(--color-primary)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    letterSpacing: '0.02em',
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>▶</span>
                  {t('grammar.nextStep')}
                </motion.button>
              )}
            </div>
          );
        })()}

      </div>

      {/* ConfirmationModal principal pour toutes les confirmations d'exit */}
      <ConfirmationModal
        isOpen={showExitConfirmation}
        onConfirm={handleExitConfirm}
        onCancel={handleExitCancel}
        message={t('learnMode.exitConfirm.message') as string}
        title={t('learnMode.exitConfirm.title') as string}
        confirmText={t('learnMode.exitConfirm.confirm') as string}
        cancelText={t('learnMode.exitConfirm.cancel') as string}
        confirmButtonStyle="danger"
        position="top"
      />
    </>
  );
};

export default GrammarMode;
